import { useState, useEffect, Suspense, useCallback, useRef } from 'react'
import { api } from '../lib/api'
import { LazyMDEditor, LazyMDEditorMarkdown, EditorSkeleton } from '../components/LazyMDEditor'
import SuggestedControlsPanel from '../components/SuggestedControlsPanel'
import { DocumentExport, BulkDocumentExport } from '../components/DocumentExport'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmModal'
import { useAuth } from '../hooks/useAuth'
import { useDatePreferences } from '../hooks/useDatePreferences'
import {
  FileText,
  Sparkles,
  Upload,
  Archive,
  Edit3,
  History,
  Download,
  X,
  Loader2,
  CheckCircle,
  Clock,
  Trash2,
  Eye,
  Pencil,
  RefreshCw,
  Link2,
  Plus,
  ChevronDown,
  ChevronUp,
  FileDown,
  FolderOpen,
  FileArchive,
  CheckSquare,
  Square,
  AlertTriangle,
  AlertCircle,
  Calendar,
  Folder,
  FolderPlus,
  ChevronRight,
  MoreVertical,
  Lock,
  Unlock,
  Users,
  MessageSquare,
  Send,
  Bell,
  GitMerge,
  GitCompareArrows,
  Copy,
  LayoutTemplate,
  Variable,
  ShieldCheck,
  UserCheck,
  XCircle,
  Undo2,
  Minus,
} from 'lucide-react'

type Document = {
  id: string
  title: string
  description: string | null
  category: string
  category_id: string | null
  category_name: string | null
  category_color: string | null
  framework_id: string | null
  framework_name: string | null
  frameworks: Array<{ id: string; name: string; display_name: string }>
  status: 'draft' | 'in-review' | 'approved' | 'archived'
  current_version: number
  review_frequency: string | null
  last_review_date: number | null
  next_review_date: number | null
  expires_at: number | null
  expiration_reason: string | null
  successor_document_id: string | null
  owner_id: string | null
  owner_username: string | null
  created_at: number
  updated_at: number
  // Collaborative editing fields (Issue #177)
  is_locked?: boolean
  locked_by_name?: string | null
  // Document Approval Workflow (Issue #50)
  approval_status?: 'not_required' | 'pending' | 'approved' | 'rejected'
  requires_approval?: boolean
  approver_id?: string | null
  approver_name?: string | null
}

type DocumentCategory = {
  id: string
  name: string
  description: string | null
  parent_id: string | null
  sort_order: number
  color: string | null
  icon: string | null
  document_count: number
  child_count: number
  children?: DocumentCategory[]
}

// Helper function to calculate expiration status
function getExpirationStatus(expiresAt: number | null): 'expired' | 'expiring_soon' | 'active' | null {
  if (!expiresAt) return null
  const now = Date.now()
  const thirtyDaysFromNow = now + (30 * 24 * 60 * 60 * 1000)
  if (expiresAt < now) return 'expired'
  if (expiresAt <= thirtyDaysFromNow) return 'expiring_soon'
  return 'active'
}

function getDaysUntilExpiration(expiresAt: number | null): number | null {
  if (!expiresAt) return null
  const now = Date.now()
  if (expiresAt < now) return 0
  return Math.ceil((expiresAt - now) / (24 * 60 * 60 * 1000))
}

// Helper function to calculate review status (#102)
function getReviewStatus(nextReviewDate: number | null): 'overdue' | 'due_soon' | 'upcoming' | null {
  if (!nextReviewDate) return null
  const now = Date.now()
  const sevenDaysFromNow = now + (7 * 24 * 60 * 60 * 1000)
  if (nextReviewDate < now) return 'overdue'
  if (nextReviewDate < sevenDaysFromNow) return 'due_soon'
  return 'upcoming'
}

function getDaysUntilReview(nextReviewDate: number | null): number | null {
  if (!nextReviewDate) return null
  const now = Date.now()
  return Math.ceil((nextReviewDate - now) / (24 * 60 * 60 * 1000))
}

// Review frequency labels
const reviewFrequencyLabels: Record<string, string> = {
  'monthly': 'Monthly',
  'quarterly': 'Quarterly',
  'semi-annually': 'Semi-Annually',
  'annually': 'Annually',
  'biennial': 'Every 2 Years',
}

type Framework = {
  id: string
  name: string
  display_name: string
}

type LinkedControl = {
  id: string
  control_id: string
  document_id: string
  control_number: string
  name: string
  description: string | null
  status: string
  implementation_status: string | null
  framework_id: string
  framework_name: string
  framework_identifier: string
  linked_at: number
}

type DocumentTemplate = {
  id: string
  name: string
  description: string | null
  content: string
  category: string
  tags: string[]
  preview: string | null
  use_count: number
  created_by: string
  created_by_username: string | null
  created_by_first_name: string | null
  created_by_last_name: string | null
  created_at: number
  updated_at: number
}

type TemplateVariable = {
  name: string
  description: string
  example: string
}

// Document Approval Workflow types (Issue #50)
type DocumentApproval = {
  id: string
  version: number
  approver_id: string
  approver_name?: string
  approver_email?: string
  status: 'pending' | 'approved' | 'rejected' | 'recalled'
  requested_by: string
  requester_name?: string
  requester_email?: string
  requested_at: number
  responded_at: number | null
  comments: string | null
}

const categoryLabels: Record<string, string> = {
  'policy': 'Policy',
  'procedure': 'Procedure',
  'standard': 'Standard',
  'guideline': 'Guideline',
  'compensating-control': 'Compensating Control',
  'diagram': 'Diagram/Image',
}

const categoryColors: Record<string, string> = {
  'policy': 'bg-blue-100 text-blue-800',
  'procedure': 'bg-green-100 text-green-800',
  'standard': 'bg-purple-100 text-purple-800',
  'guideline': 'bg-yellow-100 text-yellow-800',
  'compensating-control': 'bg-orange-100 text-orange-800',
  'diagram': 'bg-pink-100 text-pink-800',
}

const statusIcons: Record<string, React.ReactNode> = {
  'draft': <Edit3 className="h-4 w-4 text-gray-500" />,
  'in-review': <Clock className="h-4 w-4 text-yellow-500" />,
  'approved': <CheckCircle className="h-4 w-4 text-green-500" />,
  'archived': <Archive className="h-4 w-4 text-gray-400" />,
}

export default function Documents() {
  const { formatDate, formatDateTime, formatLongDate } = useDatePreferences()
  const [documents, setDocuments] = useState<Document[]>([])
  const [frameworks, setFrameworks] = useState<Framework[]>([])
  const [loading, setLoading] = useState(true)
  const [showArchived, setShowArchived] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null)
  const [selectedVersion, setSelectedVersion] = useState<any>(null)
  const [versions, setVersions] = useState<any[]>([])
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  // Document Categories/Folders state (Issue #161)
  const [folders, setFolders] = useState<DocumentCategory[]>([])
  const [flatFolders, setFlatFolders] = useState<DocumentCategory[]>([])
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [showFolderModal, setShowFolderModal] = useState(false)
  const [editingFolder, setEditingFolder] = useState<DocumentCategory | null>(null)
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  // Bulk export state
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set())
  const [showBulkExport, setShowBulkExport] = useState(false)
  const [bulkExportDocs, setBulkExportDocs] = useState<Array<{
    id: string
    title: string
    content: string
    category: string
    version: number
    lastModified?: string
    author?: string
  }>>([])
  const [loadingBulkContent, setLoadingBulkContent] = useState(false)
  // Document Templates state (Issue #92)
  const [templates, setTemplates] = useState<DocumentTemplate[]>([])
  const [showTemplatesTab, setShowTemplatesTab] = useState(false)
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [showFromTemplateModal, setShowFromTemplateModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<DocumentTemplate | null>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplate | null>(null)
  const [templateVariables, setTemplateVariables] = useState<TemplateVariable[]>([])
  const toast = useToast()
  const confirm = useConfirm()
  const { user } = useAuth()

  useEffect(() => {
    loadDocuments()
    loadFrameworks()
    loadFolders()
  }, [showArchived, selectedFolderId])

  // Filter documents by category
  const filteredDocuments = categoryFilter === 'all'
    ? documents
    : documents.filter(doc => doc.category === categoryFilter)

  // Get category counts for filter badges
  const categoryCounts = documents.reduce((acc, doc) => {
    acc[doc.category] = (acc[doc.category] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  const loadDocuments = async () => {
    try {
      const params: { status?: string; category_id?: string; uncategorized?: boolean } = {
        status: showArchived ? 'archived' : 'active'
      }
      if (selectedFolderId === 'uncategorized') {
        params.uncategorized = true
      } else if (selectedFolderId) {
        params.category_id = selectedFolderId
      }
      const response = await api.getDocuments(params)
      if (response.success) {
        setDocuments(response.documents)
      }
    } catch (error) {
      console.error('Failed to load documents:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadFrameworks = async () => {
    try {
      const response = await api.getFrameworks()
      if (response.success) {
        setFrameworks(response.frameworks)
      }
    } catch (error) {
      console.error('Failed to load frameworks:', error)
    }
  }

  const loadFolders = async () => {
    try {
      const response = await api.getDocumentCategories()
      if (response.success) {
        setFolders(response.categories)
        setFlatFolders(response.flat)
      }
    } catch (error) {
      console.error('Failed to load folders:', error)
    }
  }

  // Template functions (Issue #92)
  const loadTemplates = async () => {
    try {
      const response = await api.getDocumentTemplates()
      if (response.success) {
        setTemplates(response.templates)
      }
    } catch (error) {
      console.error('Failed to load templates:', error)
    }
  }

  const loadTemplateVariables = async () => {
    try {
      const response = await api.getTemplateVariables()
      if (response.success) {
        setTemplateVariables(response.variables)
      }
    } catch (error) {
      console.error('Failed to load template variables:', error)
    }
  }

  const handleCreateTemplate = async (data: {
    name: string
    description?: string
    content: string
    category?: string
    tags?: string[]
  }) => {
    try {
      const response = await api.createDocumentTemplate(data)
      if (response.success) {
        toast.success('Template created')
        loadTemplates()
        setShowTemplateModal(false)
      }
    } catch (error) {
      console.error('Failed to create template:', error)
      toast.error('Failed to create template')
    }
  }

  const handleUpdateTemplate = async (id: string, data: {
    name?: string
    description?: string
    content?: string
    category?: string
    tags?: string[]
  }) => {
    try {
      const response = await api.updateDocumentTemplate(id, data)
      if (response.success) {
        toast.success('Template updated')
        loadTemplates()
        setShowTemplateModal(false)
        setEditingTemplate(null)
      }
    } catch (error) {
      console.error('Failed to update template:', error)
      toast.error('Failed to update template')
    }
  }

  const handleDeleteTemplate = async (template: DocumentTemplate) => {
    const confirmed = await confirm({
      title: 'Delete Template',
      message: `Are you sure you want to delete "${template.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger',
    })

    if (!confirmed) return

    try {
      const response = await api.deleteDocumentTemplate(template.id)
      if (response.success) {
        toast.success('Template deleted')
        loadTemplates()
      }
    } catch (error) {
      console.error('Failed to delete template:', error)
      toast.error('Failed to delete template')
    }
  }

  const handleCreateFromTemplate = async (
    templateId: string,
    data: {
      title: string
      description?: string
      category_id?: string
      framework_id?: string
      framework_ids?: string[]
      review_frequency?: string
      variables?: Record<string, string>
    }
  ) => {
    try {
      const response = await api.createDocumentFromTemplate(templateId, data)
      if (response.success) {
        toast.success(response.message)
        loadDocuments()
        setShowFromTemplateModal(false)
        setSelectedTemplate(null)
      }
    } catch (error) {
      console.error('Failed to create document from template:', error)
      toast.error('Failed to create document from template')
    }
  }

  // Load templates when switching to templates tab
  useEffect(() => {
    if (showTemplatesTab) {
      loadTemplates()
      loadTemplateVariables()
    }
  }, [showTemplatesTab])

  const toggleFolderExpand = (folderId: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev)
      if (next.has(folderId)) {
        next.delete(folderId)
      } else {
        next.add(folderId)
      }
      return next
    })
  }

  const handleCreateFolder = async (name: string, parentId?: string) => {
    try {
      const response = await api.createDocumentCategory({ name, parent_id: parentId })
      if (response.success) {
        toast.success('Folder created')
        loadFolders()
      }
    } catch (error) {
      console.error('Failed to create folder:', error)
      toast.error('Failed to create folder')
    }
  }

  const handleRenameFolder = async (id: string, name: string) => {
    try {
      const response = await api.updateDocumentCategory(id, { name })
      if (response.success) {
        toast.success('Folder renamed')
        loadFolders()
      }
    } catch (error) {
      console.error('Failed to rename folder:', error)
      toast.error('Failed to rename folder')
    }
  }

  const handleDeleteFolder = async (folder: DocumentCategory) => {
    const confirmed = await confirm({
      title: 'Delete Folder',
      message: `Are you sure you want to delete "${folder.name}"? ${folder.document_count > 0 ? `${folder.document_count} document(s) will become uncategorized.` : ''} ${folder.child_count > 0 ? `${folder.child_count} subfolder(s) will move to root level.` : ''}`,
      type: 'danger',
      confirmText: 'Delete',
    })
    if (!confirmed) return
    try {
      const response = await api.deleteDocumentCategory(folder.id)
      if (response.success) {
        toast.success('Folder deleted')
        if (selectedFolderId === folder.id) {
          setSelectedFolderId(null)
        }
        loadFolders()
        loadDocuments()
      }
    } catch (error) {
      console.error('Failed to delete folder:', error)
      toast.error('Failed to delete folder')
    }
  }

  const handleMoveDocument = async (docId: string, folderId: string | null) => {
    try {
      await api.updateDocument(docId, { category_id: folderId })
      toast.success('Document moved')
      loadDocuments()
      loadFolders()
    } catch (error) {
      console.error('Failed to move document:', error)
      toast.error('Failed to move document')
    }
  }

  const handleViewDocument = async (doc: Document) => {
    setSelectedDocument(doc)
    try {
      const response = await api.getDocument(doc.id)
      if (response.success) {
        setVersions(response.versions)
        if (response.versions.length > 0) {
          setSelectedVersion(response.versions[0])
        }
      }
      setShowViewModal(true)
    } catch (error) {
      console.error('Failed to load document:', error)
    }
  }

  const handleViewHistory = async (doc: Document) => {
    setSelectedDocument(doc)
    try {
      const response = await api.getDocument(doc.id)
      if (response.success) {
        setVersions(response.versions)
      }
      setShowHistoryModal(true)
    } catch (error) {
      console.error('Failed to load history:', error)
    }
  }

  const handleArchive = async (doc: Document) => {
    const confirmed = await confirm({
      title: 'Archive Document',
      message: `Are you sure you want to archive "${doc.title}"?`,
      type: 'warning',
      confirmText: 'Archive',
    })
    if (!confirmed) return
    try {
      await api.updateDocument(doc.id, { status: 'archived' })
      toast.success('Document archived successfully')
      await loadDocuments()
    } catch (error) {
      console.error('Failed to archive document:', error)
      toast.error('Failed to archive document')
    }
  }

  const handleDelete = async (doc: Document) => {
    const confirmed = await confirm({
      title: 'Delete Document',
      message: `Are you sure you want to permanently delete "${doc.title}"? This action cannot be undone.`,
      type: 'danger',
      confirmText: 'Delete',
    })
    if (!confirmed) return
    try {
      await api.deleteDocument(doc.id)
      toast.success('Document deleted successfully')
      await loadDocuments()
    } catch (error) {
      console.error('Failed to delete document:', error)
      toast.error('Failed to delete document')
    }
  }

  const formatDocDate = (timestamp: number | null) => {
    if (!timestamp) return '-'
    return formatDate(timestamp)
  }

  // Bulk selection handlers
  const toggleDocSelection = (docId: string) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev)
      if (next.has(docId)) {
        next.delete(docId)
      } else {
        next.add(docId)
      }
      return next
    })
  }

  const selectAllDocs = () => {
    if (selectedDocIds.size === filteredDocuments.length) {
      setSelectedDocIds(new Set())
    } else {
      setSelectedDocIds(new Set(filteredDocuments.map((d) => d.id)))
    }
  }

  const clearSelection = () => {
    setSelectedDocIds(new Set())
  }

  // Trigger bulk export - loads content for all selected documents
  const handleBulkExport = async () => {
    if (selectedDocIds.size === 0) {
      toast.error('Please select documents to export')
      return
    }

    setLoadingBulkContent(true)

    try {
      const docsToExport: typeof bulkExportDocs = []

      for (const docId of selectedDocIds) {
        const doc = documents.find((d) => d.id === docId)
        if (!doc) continue

        // Fetch document content
        const response = await api.getDocument(docId)
        if (response.success && response.versions?.length > 0) {
          const latestVersion = response.versions[0]
          docsToExport.push({
            id: doc.id,
            title: doc.title,
            content: latestVersion.content || '',
            category: doc.category,
            version: doc.current_version,
            lastModified: formatDate(doc.updated_at),
            author: doc.owner_username || undefined,
          })
        }
      }

      setBulkExportDocs(docsToExport)
      setShowBulkExport(true)
    } catch (error) {
      console.error('Failed to load document contents:', error)
      toast.error('Failed to load document contents for export')
    } finally {
      setLoadingBulkContent(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Document Center</h1>
          <p className="text-gray-500 mt-1">
            Manage policies, procedures, and compliance documents
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Bulk Export Button */}
          <button
            onClick={handleBulkExport}
            disabled={selectedDocIds.size === 0 || loadingBulkContent}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border bg-white border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingBulkContent ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileArchive className="h-4 w-4" />
            )}
            Export{selectedDocIds.size > 0 && ` (${selectedDocIds.size})`}
          </button>
          <button
            onClick={() => setShowTemplatesTab(!showTemplatesTab)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
              showTemplatesTab
                ? 'bg-primary-100 border-primary-300 text-primary-700'
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <LayoutTemplate className="h-4 w-4" />
            Templates
          </button>
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`px-4 py-2 rounded-lg border ${
              showArchived
                ? 'bg-gray-100 border-gray-300 text-gray-700'
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {showArchived ? 'View Active' : 'View Archived'}
          </button>
        </div>
      </div>

      {/* Templates Section (Issue #92) */}
      {showTemplatesTab && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <LayoutTemplate className="h-5 w-5 text-primary-600" />
              <h3 className="font-medium text-gray-900">Document Templates</h3>
              <span className="text-sm text-gray-500">({templates.length})</span>
            </div>
            {user && ['admin', 'contributor'].includes(user.role) && (
              <button
                onClick={() => { setEditingTemplate(null); setShowTemplateModal(true) }}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                New Template
              </button>
            )}
          </div>

          {templates.length === 0 ? (
            <div className="text-center py-12">
              <LayoutTemplate className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No templates yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Create reusable document templates with variable placeholders
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <LayoutTemplate className="h-5 w-5 text-gray-400" />
                      <h4 className="font-medium text-gray-900 truncate" title={template.name}>
                        {template.name}
                      </h4>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${categoryColors[template.category] || 'bg-gray-100 text-gray-600'}`}>
                      {categoryLabels[template.category] || template.category}
                    </span>
                  </div>
                  {template.description && (
                    <p className="text-sm text-gray-500 mb-2 line-clamp-2">{template.description}</p>
                  )}
                  {template.preview && (
                    <p className="text-xs text-gray-400 mb-3 line-clamp-2 italic">{template.preview}</p>
                  )}
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>Used {template.use_count} times</span>
                    <span>{formatDate(template.updated_at)}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => { setSelectedTemplate(template); setShowFromTemplateModal(true) }}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Use Template
                    </button>
                    {user && ['admin', 'contributor'].includes(user.role) && (
                      <>
                        <button
                          onClick={() => { setEditingTemplate(template); setShowTemplateModal(true) }}
                          className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                          title="Edit template"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template)}
                          className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                          title="Delete template"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Available Variables Info */}
          {templateVariables.length > 0 && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Variable className="h-4 w-4 text-gray-500" />
                <h4 className="text-sm font-medium text-gray-700">Available Template Variables</h4>
              </div>
              <p className="text-xs text-gray-500 mb-2">Use {"{{variable_name}}"} syntax in your templates:</p>
              <div className="flex flex-wrap gap-2">
                {templateVariables.map((v) => (
                  <span
                    key={v.name}
                    className="px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-600"
                    title={`${v.description} (e.g., ${v.example})`}
                  >
                    {`{{${v.name}}}`}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Folders Section (Issue #161) */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Folder className="h-5 w-5 text-gray-500" />
            <h3 className="font-medium text-gray-900">Folders</h3>
          </div>
          <button
            onClick={() => { setEditingFolder(null); setShowFolderModal(true) }}
            className="flex items-center gap-1 px-2 py-1 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          >
            <FolderPlus className="h-4 w-4" />
            New Folder
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setSelectedFolderId(null)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              selectedFolderId === null
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            All Documents
          </button>
          <button
            onClick={() => setSelectedFolderId('uncategorized')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              selectedFolderId === 'uncategorized'
                ? 'bg-gray-200 text-gray-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Uncategorized
          </button>
          {flatFolders.map((folder) => (
            <div key={folder.id} className="relative group">
              <button
                onClick={() => setSelectedFolderId(folder.id)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  selectedFolderId === folder.id
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                style={folder.color ? { borderLeft: `3px solid ${folder.color}` } : undefined}
              >
                {folder.parent_id && <span className="text-gray-400 mr-1">-</span>}
                <Folder className="h-3.5 w-3.5" />
                {folder.name}
                <span className="ml-1 text-xs text-gray-400">
                  ({folder.document_count})
                </span>
              </button>
              <div className="absolute right-0 top-0 hidden group-hover:flex items-center">
                <button
                  onClick={(e) => { e.stopPropagation(); setEditingFolder(folder); setShowFolderModal(true) }}
                  className="p-1 text-gray-400 hover:text-gray-600"
                  title="Edit folder"
                >
                  <Pencil className="h-3 w-3" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder) }}
                  className="p-1 text-gray-400 hover:text-red-600"
                  title="Delete folder"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Category Filter Tabs */}
      <div className="bg-white rounded-lg border border-gray-200 p-2">
        <div className="flex items-center gap-2 overflow-x-auto">
          <div className="flex items-center gap-1 text-gray-500 px-2">
            <FolderOpen className="h-4 w-4" />
            <span className="text-sm font-medium">Document Types:</span>
          </div>
          <button
            onClick={() => setCategoryFilter('all')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              categoryFilter === 'all'
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            All
            <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
              {documents.length}
            </span>
          </button>
          {Object.entries(categoryLabels).map(([value, label]) => {
            const count = categoryCounts[value] || 0
            if (count === 0 && categoryFilter !== value) return null
            return (
              <button
                key={value}
                onClick={() => setCategoryFilter(value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  categoryFilter === value
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {label}
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${categoryColors[value]}`}>
                  {count}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Create/Import */}
        <div className="space-y-6">
          {/* Generate All Documents */}
          <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl p-6 text-white">
            <h2 className="text-lg font-semibold mb-2">Generate All Documents</h2>
            <p className="text-primary-100 text-sm mb-4">
              Auto-generate a complete set of policies and procedures for a compliance framework
            </p>
            <GenerateAllForm
              frameworks={frameworks}
              onSuccess={() => {
                loadDocuments()
              }}
            />
          </div>

          {/* Generate with AI */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Generate Single Document</h2>
            <GenerateForm
              frameworks={frameworks}
              onSuccess={() => {
                loadDocuments()
              }}
            />
          </div>

          {/* Import */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Import Document</h2>
            <ImportForm
              frameworks={frameworks}
              onSuccess={() => {
                loadDocuments()
              }}
            />
          </div>
        </div>

        {/* Right Column - Document List */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Select All Checkbox */}
                <button
                  onClick={selectAllDocs}
                  className="flex items-center justify-center p-1 rounded hover:bg-gray-100 transition-colors"
                  title={selectedDocIds.size === filteredDocuments.length ? 'Deselect all' : 'Select all'}
                >
                  {selectedDocIds.size === filteredDocuments.length && filteredDocuments.length > 0 ? (
                    <CheckSquare className="h-5 w-5 text-primary-600" />
                  ) : selectedDocIds.size > 0 ? (
                    <div className="h-5 w-5 border-2 border-primary-600 rounded flex items-center justify-center">
                      <div className="h-2 w-2 bg-primary-600 rounded-sm" />
                    </div>
                  ) : (
                    <Square className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                <h2 className="text-lg font-semibold text-gray-900">
                  {showArchived ? 'Archived Documents' : 'Active Documents'}
                </h2>
              </div>
              {selectedDocIds.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">{selectedDocIds.size} selected</span>
                  <button
                    onClick={clearSelection}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>

            {filteredDocuments.length === 0 ? (
              <div className="p-12 text-center">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">
                  {categoryFilter !== 'all'
                    ? `No ${categoryLabels[categoryFilter] || categoryFilter} documents found`
                    : showArchived
                    ? 'No archived documents'
                    : 'No documents yet. Generate or import your first document!'}
                </p>
                {categoryFilter !== 'all' && (
                  <button
                    onClick={() => setCategoryFilter('all')}
                    className="mt-4 text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    View all documents
                  </button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    className={`p-6 hover:bg-gray-50 ${selectedDocIds.has(doc.id) ? 'bg-primary-50' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      {/* Row Checkbox */}
                      <button
                        onClick={() => toggleDocSelection(doc.id)}
                        className="flex items-center justify-center p-1 mr-3 mt-1 rounded hover:bg-gray-100 transition-colors flex-shrink-0"
                      >
                        {selectedDocIds.has(doc.id) ? (
                          <CheckSquare className="h-5 w-5 text-primary-600" />
                        ) : (
                          <Square className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900 truncate">{doc.title}</h3>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${categoryColors[doc.category]}`}>
                            {categoryLabels[doc.category]}
                          </span>
                          {/* Show all framework badges */}
                          {doc.frameworks && doc.frameworks.length > 0 ? (
                            doc.frameworks.map((fw) => (
                              <span
                                key={fw.id}
                                className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700"
                                title={fw.display_name || fw.name}
                              >
                                {fw.display_name || fw.name}
                              </span>
                            ))
                          ) : doc.framework_name ? (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                              {doc.framework_name}
                            </span>
                          ) : null}
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                            v{doc.current_version}
                          </span>
                          {/* Lock indicator (Issue #177) */}
                          {doc.is_locked && (
                            <span
                              className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700"
                              title={`Being edited by ${doc.locked_by_name}`}
                            >
                              <Lock className="h-3 w-3" />
                              {doc.locked_by_name}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                          <span className="flex items-center gap-1">
                            {statusIcons[doc.status]}
                            <span className="capitalize">{doc.status.replace('-', ' ')}</span>
                          </span>
                          <span>Next Review: {formatDate(doc.next_review_date)}</span>
                          <span>Last Reviewed: {formatDate(doc.last_review_date)}</span>
                          {/* Expiration indicator */}
                          {doc.expires_at && (() => {
                            const expStatus = getExpirationStatus(doc.expires_at)
                            const daysLeft = getDaysUntilExpiration(doc.expires_at)
                            if (expStatus === 'expired') {
                              return (
                                <span className="flex items-center gap-1 text-red-600 font-medium">
                                  <AlertTriangle className="h-4 w-4" />
                                  Expired
                                </span>
                              )
                            } else if (expStatus === 'expiring_soon') {
                              return (
                                <span className="flex items-center gap-1 text-amber-600 font-medium">
                                  <Clock className="h-4 w-4" />
                                  Expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                                </span>
                              )
                            } else {
                              return (
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-4 w-4" />
                                  Expires: {formatDate(doc.expires_at)}
                                </span>
                              )
                            }
                          })()}
                        </div>
                        {doc.description && (
                          <p className="text-sm text-gray-600 mt-2 line-clamp-2">{doc.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleViewHistory(doc)}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                          title="View History"
                        >
                          <History className="h-4 w-4" />
                        </button>
                        {!showArchived && (
                          <button
                            onClick={() => handleArchive(doc)}
                            className="p-2 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg"
                            title="Archive"
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                        )}
                        {showArchived && (
                          <button
                            onClick={() => handleDelete(doc)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete permanently"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleViewDocument(doc)}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg"
                        >
                          View & Edit
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* View/Edit Modal */}
      {showViewModal && selectedDocument && (
        <ViewEditModal
          document={selectedDocument}
          version={selectedVersion}
          versions={versions}
          onClose={() => {
            setShowViewModal(false)
            setSelectedDocument(null)
            setSelectedVersion(null)
          }}
          onSave={() => {
            loadDocuments()
          }}
          onVersionChange={(v) => setSelectedVersion(v)}
          onSaveAsTemplate={async (data) => {
            const response = await api.createDocumentTemplate(data)
            if (response.success) {
              loadTemplates()
            } else {
              throw new Error('Failed to create template')
            }
          }}
        />
      )}

      {/* History Modal */}
      {showHistoryModal && selectedDocument && (
        <HistoryModal
          document={selectedDocument}
          versions={versions}
          onClose={() => {
            setShowHistoryModal(false)
            setSelectedDocument(null)
          }}
        />
      )}

      {/* Bulk Export Modal */}
      {showBulkExport && (
        <BulkDocumentExport
          documents={bulkExportDocs}
          onClose={() => {
            setShowBulkExport(false)
            setBulkExportDocs([])
          }}
          onComplete={() => {
            clearSelection()
            toast.success('Documents exported successfully')
          }}
        />
      )}

      {/* Folder Create/Edit Modal (Issue #161) */}
      {showFolderModal && (
        <FolderModal
          folder={editingFolder}
          folders={flatFolders}
          onClose={() => { setShowFolderModal(false); setEditingFolder(null) }}
          onSave={(name, parentId) => {
            if (editingFolder) {
              handleRenameFolder(editingFolder.id, name)
            } else {
              handleCreateFolder(name, parentId)
            }
            setShowFolderModal(false)
            setEditingFolder(null)
          }}
        />
      )}

      {/* Template Create/Edit Modal (Issue #92) */}
      {showTemplateModal && (
        <TemplateModal
          template={editingTemplate}
          variables={templateVariables}
          onClose={() => { setShowTemplateModal(false); setEditingTemplate(null) }}
          onSave={(data) => {
            if (editingTemplate) {
              handleUpdateTemplate(editingTemplate.id, data)
            } else {
              handleCreateTemplate(data)
            }
          }}
        />
      )}

      {/* Create from Template Modal (Issue #92) */}
      {showFromTemplateModal && selectedTemplate && (
        <CreateFromTemplateModal
          template={selectedTemplate}
          folders={flatFolders}
          frameworks={frameworks}
          variables={templateVariables}
          onClose={() => { setShowFromTemplateModal(false); setSelectedTemplate(null) }}
          onSave={(data) => handleCreateFromTemplate(selectedTemplate.id, data)}
        />
      )}
    </div>
  )
}

// Template Modal Component (Issue #92)
function TemplateModal({
  template,
  variables,
  onClose,
  onSave,
}: {
  template: DocumentTemplate | null
  variables: TemplateVariable[]
  onClose: () => void
  onSave: (data: { name: string; description?: string; content: string; category?: string; tags?: string[] }) => void
}) {
  const [name, setName] = useState(template?.name || '')
  const [description, setDescription] = useState(template?.description || '')
  const [content, setContent] = useState(template?.content || '')
  const [category, setCategory] = useState(template?.category || 'policy')
  const [tagsInput, setTagsInput] = useState(template?.tags?.join(', ') || '')
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !content.trim()) return

    setSaving(true)
    try {
      const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean)
      await onSave({ name: name.trim(), description: description.trim() || undefined, content, category, tags: tags.length > 0 ? tags : undefined })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {template ? 'Edit Template' : 'Create Template'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Access Control Policy Template"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {Object.entries(categoryLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Brief description of the template"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma separated)</label>
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="e.g., access-control, security, soc2"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">Template Content *</label>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Variable className="h-3.5 w-3.5" />
                <span>Variables: {variables.map(v => `{{${v.name}}}`).join(', ')}</span>
              </div>
            </div>
            <div className="border border-gray-300 rounded-lg overflow-hidden" style={{ minHeight: '300px' }}>
              <Suspense fallback={<EditorSkeleton />}>
                <LazyMDEditor
                  value={content}
                  onChange={(val) => setContent(val || '')}
                  preview="edit"
                  height={300}
                />
              </Suspense>
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!name.trim() || !content.trim() || saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {template ? 'Update Template' : 'Create Template'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Create from Template Modal Component (Issue #92)
function CreateFromTemplateModal({
  template,
  folders,
  frameworks,
  variables,
  onClose,
  onSave,
}: {
  template: DocumentTemplate
  folders: DocumentCategory[]
  frameworks: Framework[]
  variables: TemplateVariable[]
  onClose: () => void
  onSave: (data: {
    title: string
    description?: string
    category_id?: string
    framework_id?: string
    review_frequency?: string
    variables?: Record<string, string>
  }) => void
}) {
  const { formatLongDate } = useDatePreferences()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState(template.description || '')
  const [categoryId, setCategoryId] = useState('')
  const [frameworkId, setFrameworkId] = useState('')
  const [reviewFrequency, setReviewFrequency] = useState('')
  const [customVariables, setCustomVariables] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return

    setSaving(true)
    try {
      await onSave({
        title: title.trim(),
        description: description.trim() || undefined,
        category_id: categoryId || undefined,
        framework_id: frameworkId || undefined,
        review_frequency: reviewFrequency || undefined,
        variables: Object.keys(customVariables).length > 0 ? customVariables : undefined,
      })
    } finally {
      setSaving(false)
    }
  }

  // Preview the content with variable substitution
  const getPreviewContent = () => {
    let previewContent = template.content
    const defaultVars: Record<string, string> = {
      company_name: 'Your Organization',
      date: formatLongDate(new Date()),
      year: new Date().getFullYear().toString(),
      author: 'Current User',
      version: '1.0',
      document_title: title || 'Document Title',
    }
    const allVars = { ...defaultVars, ...customVariables }
    for (const [key, value] of Object.entries(allVars)) {
      const regex = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'gi')
      previewContent = previewContent.replace(regex, value)
    }
    return previewContent
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Create Document from Template</h2>
            <p className="text-sm text-gray-500">Using: {template.name}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="e.g., Access Control Policy v1.0"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Brief description of this document"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Folder</label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">None</option>
                {folders.map((folder) => (
                  <option key={folder.id} value={folder.id}>{folder.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Framework</label>
              <select
                value={frameworkId}
                onChange={(e) => setFrameworkId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">None</option>
                {frameworks.map((fw) => (
                  <option key={fw.id} value={fw.id}>{fw.display_name || fw.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Review Frequency</label>
              <select
                value={reviewFrequency}
                onChange={(e) => setReviewFrequency(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">None</option>
                {Object.entries(reviewFrequencyLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Custom Variable Overrides */}
          {variables.length > 0 && (
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Variable className="h-4 w-4 text-gray-500" />
                <h4 className="text-sm font-medium text-gray-700">Custom Variable Values (Optional)</h4>
              </div>
              <p className="text-xs text-gray-500 mb-3">Override default values for template variables:</p>
              <div className="grid grid-cols-2 gap-3">
                {variables.map((v) => (
                  <div key={v.name}>
                    <label className="block text-xs text-gray-600 mb-1">
                      {v.name} <span className="text-gray-400">({v.description})</span>
                    </label>
                    <input
                      type="text"
                      value={customVariables[v.name] || ''}
                      onChange={(e) => setCustomVariables(prev => ({
                        ...prev,
                        [v.name]: e.target.value
                      }))}
                      placeholder={v.example}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preview Toggle */}
          <div>
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
            >
              <Eye className="h-4 w-4" />
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
            {showPreview && (
              <div className="mt-2 border border-gray-200 rounded-lg p-4 bg-gray-50 max-h-60 overflow-y-auto">
                <Suspense fallback={<div className="animate-pulse h-20 bg-gray-200 rounded" />}>
                  <LazyMDEditorMarkdown source={getPreviewContent()} />
                </Suspense>
              </div>
            )}
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Create Document
          </button>
        </div>
      </div>
    </div>
  )
}

// Generate Form Component
function GenerateForm({
  frameworks,
  onSuccess,
}: {
  frameworks: Framework[]
  onSuccess: () => void
}) {
  const [category, setCategory] = useState('policy')
  const [frameworkId, setFrameworkId] = useState('')
  const [title, setTitle] = useState('')
  const [generating, setGenerating] = useState(false)
  const toast = useToast()

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title || !category) return

    setGenerating(true)
    try {
      const response = await api.generateDocument({
        title,
        category,
        framework_id: frameworkId || undefined,
      })
      if (response.success) {
        // Check if this was a fallback (template) generation
        if (response.generation?.isFallback) {
          toast.warning(
            'Document created with template. AI was temporarily unavailable - please customize the placeholder content.',
            6000
          )
        } else if (response.generation?.cached) {
          toast.success('Document generated from cache!')
        } else {
          toast.success('Document generated successfully!')
        }
        setTitle('')
        onSuccess()
      }
    } catch (error) {
      console.error('Failed to generate document:', error)
      toast.error('Failed to generate document')
    } finally {
      setGenerating(false)
    }
  }

  return (
    <form onSubmit={handleGenerate} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        >
          {Object.entries(categoryLabels).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Compliance Framework</label>
        <select
          value={frameworkId}
          onChange={(e) => setFrameworkId(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        >
          <option value="">Select a framework...</option>
          {frameworks.map((fw) => (
            <option key={fw.id} value={fw.id}>{fw.display_name || fw.name}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Document Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Information Security Policy"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          required
        />
      </div>

      <button
        type="submit"
        disabled={generating || !title}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
      >
        {generating ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Generating...
          </>
        ) : (
          <>
            <Sparkles className="h-5 w-5" />
            Generate with AI
          </>
        )}
      </button>
    </form>
  )
}

// Generate All Form Component
function GenerateAllForm({
  frameworks,
  onSuccess,
}: {
  frameworks: Framework[]
  onSuccess: () => void
}) {
  const [frameworkId, setFrameworkId] = useState('')
  const [generating, setGenerating] = useState(false)
  const [progress, setProgress] = useState<{
    show: boolean
    total: number
    created: number
    mapped: number
    skipped: number
    error: number
    fallback: number
    results: Array<{ title: string; status: 'created' | 'mapped' | 'skipped' | 'error'; message?: string; isFallback?: boolean }>
  }>({ show: false, total: 0, created: 0, mapped: 0, skipped: 0, error: 0, fallback: 0, results: [] })
  const toast = useToast()

  const handleGenerateAll = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!frameworkId) return

    setGenerating(true)
    setProgress({ show: true, total: 0, created: 0, mapped: 0, skipped: 0, error: 0, fallback: 0, results: [] })

    try {
      const response = await api.generateAllDocuments(frameworkId)
      if (response.success) {
        const fallbackCount = response.summary.fallback || 0
        setProgress({
          show: true,
          total: response.summary.total,
          created: response.summary.created || 0,
          mapped: response.summary.mapped || 0,
          skipped: response.summary.skipped || 0,
          error: response.summary.error || 0,
          fallback: fallbackCount,
          results: response.results,
        })
        // Show appropriate toast based on results
        if (fallbackCount > 0) {
          toast.warning(
            `${fallbackCount} document(s) created with templates (AI temporarily unavailable). Please review and customize.`,
            6000
          )
        }
        onSuccess()
      }
    } catch (error) {
      console.error('Failed to generate documents:', error)
      toast.error('Failed to generate documents')
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div className="space-y-4">
      <form onSubmit={handleGenerateAll} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-white/90 mb-1">Select Framework</label>
          <select
            value={frameworkId}
            onChange={(e) => setFrameworkId(e.target.value)}
            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-white/50 focus:border-transparent"
            disabled={generating}
          >
            <option value="" className="text-gray-900">Choose a framework...</option>
            {frameworks.map((fw) => (
              <option key={fw.id} value={fw.id} className="text-gray-900">
                {fw.display_name || fw.name}
              </option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          disabled={generating || !frameworkId}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white text-primary-700 rounded-lg hover:bg-white/90 disabled:opacity-50 font-medium"
        >
          {generating ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Processing Documents...
            </>
          ) : (
            <>
              <Sparkles className="h-5 w-5" />
              Generate All Documents
            </>
          )}
        </button>
      </form>

      {progress.show && !generating && (
        <div className="mt-4 p-4 bg-white/10 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Processing Complete</span>
            <button
              onClick={() => setProgress({ ...progress, show: false })}
              className="text-white/60 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {progress.created > 0 && (
              <span className="flex items-center gap-1">
                <CheckCircle className="h-4 w-4 text-green-300" />
                {progress.created} created
              </span>
            )}
            {progress.mapped > 0 && (
              <span className="flex items-center gap-1 text-blue-200">
                <CheckCircle className="h-4 w-4" />
                {progress.mapped} mapped
              </span>
            )}
            {progress.skipped > 0 && (
              <span className="flex items-center gap-1 text-white/60">
                {progress.skipped} skipped
              </span>
            )}
            {progress.fallback > 0 && (
              <span className="flex items-center gap-1 text-yellow-300">
                <AlertCircle className="h-4 w-4" />
                {progress.fallback} templates
              </span>
            )}
            {progress.error > 0 && (
              <span className="flex items-center gap-1 text-red-300">
                <X className="h-4 w-4" />
                {progress.error} failed
              </span>
            )}
          </div>
          {progress.mapped > 0 && (
            <div className="mt-2 text-xs text-white/70">
              Existing documents were linked to this framework instead of creating duplicates.
            </div>
          )}
          {progress.fallback > 0 && (
            <div className="mt-2 text-xs text-yellow-200">
              {progress.fallback} document(s) created with templates because AI was temporarily unavailable. Please review and customize: {progress.results.filter(r => r.isFallback).map(r => r.title).join(', ')}
            </div>
          )}
          {progress.error > 0 && (
            <div className="mt-2 text-xs text-red-200">
              Failed: {progress.results.filter(r => r.status === 'error').map(r => r.title).join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Import Form Component
function ImportForm({
  frameworks,
  onSuccess,
}: {
  frameworks: Framework[]
  onSuccess: () => void
}) {
  const [category, setCategory] = useState('policy')
  const [frameworkId, setFrameworkId] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [reviewFrequencyImport, setReviewFrequencyImport] = useState('')
  const [importing, setImporting] = useState(false)
  const toast = useToast()

  const handleImport = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file || !title) return

    setImporting(true)
    try {
      // First create the document
      const createResponse = await api.createDocument({
        title,
        category,
        framework_id: frameworkId || undefined,
        description: `Imported from: ${file.name}`,
        review_frequency: reviewFrequencyImport || undefined,
      })

      if (createResponse.success) {
        // Then upload the file
        await api.uploadDocumentFile(createResponse.document.id, file)
        toast.success('Document imported successfully!')
        setTitle('')
        setFile(null)
        onSuccess()
      }
    } catch (error) {
      console.error('Failed to import document:', error)
      toast.error('Failed to import document')
    } finally {
      setImporting(false)
    }
  }

  return (
    <form onSubmit={handleImport} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        >
          {Object.entries(categoryLabels).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Compliance Framework</label>
        <select
          value={frameworkId}
          onChange={(e) => setFrameworkId(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        >
          <option value="">Select a framework...</option>
          {frameworks.map((fw) => (
            <option key={fw.id} value={fw.id}>{fw.display_name || fw.name}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Document Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Information Security Policy"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Review Schedule</label>
        <select
          value={reviewFrequencyImport}
          onChange={(e) => setReviewFrequencyImport(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        >
          <option value="">No scheduled review</option>
          {Object.entries(reviewFrequencyLabels).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Upload Document</label>
        <input
          type="file"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          accept=".pdf,.doc,.docx,.txt,.md"
        />
        {file && <p className="text-sm text-gray-500 mt-1">{file.name}</p>}
      </div>

      <button
        type="submit"
        disabled={importing || !file || !title}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50"
      >
        {importing ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Importing...
          </>
        ) : (
          <>
            <Upload className="h-5 w-5" />
            Import File
          </>
        )}
      </button>
    </form>
  )
}

// Save as Template Form Component (Issue #92)
function SaveAsTemplateForm({
  initialName,
  initialDescription,
  initialContent,
  initialCategory,
  saving,
  onSave,
  onCancel,
}: {
  initialName: string
  initialDescription: string
  initialContent: string
  initialCategory: string
  saving: boolean
  onSave: (data: { name: string; description?: string; content: string; category: string; tags?: string[] }) => void
  onCancel: () => void
}) {
  const [name, setName] = useState(initialName)
  const [description, setDescription] = useState(initialDescription)
  const [content, setContent] = useState(initialContent)
  const [category, setCategory] = useState(initialCategory)
  const [tagsInput, setTagsInput] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return

    const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean)
    onSave({
      name: name.trim(),
      description: description.trim() || undefined,
      content,
      category,
      tags: tags.length > 0 ? tags : undefined,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Template Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Information Security Policy Template"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description of this template"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Category
        </label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="policy">Policy</option>
          <option value="procedure">Procedure</option>
          <option value="standard">Standard</option>
          <option value="guideline">Guideline</option>
          <option value="compensating-control">Compensating Control</option>
          <option value="diagram">Diagram/Image</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Tags <span className="text-xs text-gray-500">(comma separated)</span>
        </label>
        <input
          type="text"
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
          placeholder="e.g., security, compliance, pci-dss"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
        <p className="text-xs text-gray-600 mb-2">
          <Variable className="h-3 w-3 inline mr-1" />
          <strong>Template Variables:</strong> You can use placeholders like {"{{company_name}}"}, {"{{date}}"}, {"{{author}}"} in the content. These will be replaced when creating documents from this template.
        </p>
        <p className="text-xs text-gray-500">
          Content length: {content.length} characters
        </p>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <LayoutTemplate className="h-4 w-4" />
              Create Template
            </>
          )}
        </button>
      </div>
    </form>
  )
}

// View/Edit Modal Component with Rich Markdown Editor
function ViewEditModal({
  document,
  version,
  versions,
  onClose,
  onSave,
  onVersionChange,
  onSaveAsTemplate,
}: {
  document: Document
  version: any
  versions: any[]
  onClose: () => void
  onSave: () => void
  onVersionChange: (v: any) => void
  onSaveAsTemplate?: (data: { name: string; description?: string; content: string; category?: string; tags?: string[] }) => Promise<void>
}) {
  const { formatDate, formatDateTime } = useDatePreferences()
  const [content, setContent] = useState(version?.content || '')
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [status, setStatus] = useState(document.status)
  const [showExport, setShowExport] = useState(false)
  const [showSaveAsTemplateModal, setShowSaveAsTemplateModal] = useState(false)
  const [savingAsTemplate, setSavingAsTemplate] = useState(false)
  const [showExpirationPanel, setShowExpirationPanel] = useState(false)
  const [expiresAt, setExpiresAt] = useState<string>(
    document.expires_at ? new Date(document.expires_at).toISOString().split('T')[0] : ''
  )
  const [expirationReason, setExpirationReason] = useState(document.expiration_reason || '')
  // Review schedule state (#102)
  const [showReviewPanel, setShowReviewPanel] = useState(false)
  const [reviewFrequency, setReviewFrequency] = useState(document.review_frequency || '')

  // Collaborative editing state (Issue #177)
  const [lockStatus, setLockStatus] = useState<{
    is_locked: boolean
    lock: {
      locked_by: string
      locked_by_username: string
      locked_by_name: string
      locked_at: number
      expires_at: number
      expires_in_ms: number
    } | null
    active_editors: Array<{
      session_id: string
      user_id: string
      username: string
      name: string
      started_at: number
      last_heartbeat: number
    }>
  } | null>(null)
  const [hasLock, setHasLock] = useState(false)
  const [acquiringLock, setAcquiringLock] = useState(false)
  const [showComments, setShowComments] = useState(false)
  const [comments, setComments] = useState<Array<{
    id: string
    user_id: string
    author_name: string
    comment_text: string
    status: 'open' | 'resolved' | 'accepted' | 'rejected'
    created_at: number
  }>>([])
  const [newComment, setNewComment] = useState('')
  const [loadingComments, setLoadingComments] = useState(false)
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Conflict detection state (Issue #177)
  const [startingVersion, setStartingVersion] = useState<number>(version?.version || document.current_version)
  const [conflictData, setConflictData] = useState<{
    conflict_type: 'locked' | 'version_conflict'
    locked_by?: string
    lock_expires_at?: number
    expected_version?: number
    current_version?: number
    current_content?: string
    conflicting_edit?: {
      version: number
      created_by: string
      created_by_name: string
      created_at: number
      change_summary: string | null
    }
  } | null>(null)
  const [showConflictModal, setShowConflictModal] = useState(false)

  // Document Approval Workflow state (Issue #50)
  const [showApprovalPanel, setShowApprovalPanel] = useState(false)
  const [approvals, setApprovals] = useState<DocumentApproval[]>([])
  const [loadingApprovals, setLoadingApprovals] = useState(false)
  const [requestingApproval, setRequestingApproval] = useState(false)
  const [selectedApproverId, setSelectedApproverId] = useState('')
  const [approvalComments, setApprovalComments] = useState('')
  const [users, setUsers] = useState<Array<{ id: string; username: string; first_name?: string; last_name?: string; role: string }>>([])

  const toast = useToast()
  const confirm = useConfirm()
  const { user } = useAuth()

  // Load lock status on mount
  useEffect(() => {
    loadLockStatus()

    // Poll for lock status updates every 30 seconds
    const pollInterval = setInterval(loadLockStatus, 30000)

    return () => {
      clearInterval(pollInterval)
      // Clear heartbeat interval on unmount
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current)
      }
    }
  }, [document.id])

  const loadLockStatus = async () => {
    try {
      const response = await api.getDocumentLockStatus(document.id)
      if (response.success) {
        setLockStatus({
          is_locked: response.is_locked,
          lock: response.lock,
          active_editors: response.active_editors,
        })
        // Check if current user has the lock
        setHasLock(response.is_locked && response.lock?.locked_by === user?.id)
      }
    } catch (error) {
      console.error('Failed to load lock status:', error)
    }
  }

  // Handle acquiring lock for editing
  const handleAcquireLock = async () => {
    setAcquiringLock(true)
    try {
      const response = await api.lockDocument(document.id)
      if (response.success) {
        setHasLock(true)
        toast.success('Edit lock acquired')
        loadLockStatus()

        // Start heartbeat to keep lock alive
        heartbeatIntervalRef.current = setInterval(async () => {
          try {
            await api.documentHeartbeat(document.id)
          } catch (error) {
            console.error('Heartbeat failed:', error)
          }
        }, 60000) // Every 60 seconds
      }
    } catch (error: any) {
      console.error('Failed to acquire lock:', error)
      if (error.response?.status === 409) {
        toast.error(`Document is being edited by ${error.response?.data?.locked_by || 'another user'}`)
      } else {
        toast.error('Failed to acquire edit lock')
      }
      loadLockStatus()
    } finally {
      setAcquiringLock(false)
    }
  }

  // Handle releasing lock
  const handleReleaseLock = async () => {
    try {
      const response = await api.unlockDocument(document.id)
      if (response.success) {
        setHasLock(false)
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current)
          heartbeatIntervalRef.current = null
        }
        toast.success('Edit lock released')
        loadLockStatus()
      }
    } catch (error) {
      console.error('Failed to release lock:', error)
      toast.error('Failed to release lock')
    }
  }

  // Handle force unlock (admin only)
  const handleForceUnlock = async () => {
    const confirmed = await confirm({
      title: 'Force Unlock Document',
      message: `This will forcibly remove ${lockStatus?.lock?.locked_by_name}'s edit lock. They will lose any unsaved changes. Continue?`,
      type: 'danger',
      confirmText: 'Force Unlock',
    })
    if (!confirmed) return

    try {
      const response = await api.forceUnlockDocument(document.id)
      if (response.success) {
        toast.success(`Lock removed from ${response.previous_lock_holder}`)
        loadLockStatus()
      }
    } catch (error) {
      console.error('Failed to force unlock:', error)
      toast.error('Failed to force unlock')
    }
  }

  // Handle request access
  const handleRequestAccess = async () => {
    try {
      const response = await api.requestDocumentAccess(document.id)
      if (response.success) {
        toast.success(`Access request sent to ${response.notified_user}`)
      }
    } catch (error) {
      console.error('Failed to request access:', error)
      toast.error('Failed to send access request')
    }
  }

  // Load comments
  const loadComments = async () => {
    setLoadingComments(true)
    try {
      const response = await api.getDocumentComments(document.id)
      if (response.success) {
        setComments(response.comments as any)
      }
    } catch (error) {
      console.error('Failed to load comments:', error)
    } finally {
      setLoadingComments(false)
    }
  }

  // Add comment
  const handleAddComment = async () => {
    if (!newComment.trim()) return

    try {
      await api.addDocumentComment(document.id, {
        comment_text: newComment.trim(),
      })
      setNewComment('')
      loadComments()
      toast.success('Comment added')
    } catch (error) {
      console.error('Failed to add comment:', error)
      toast.error('Failed to add comment')
    }
  }

  // Resolve comment
  const handleResolveComment = async (commentId: string) => {
    try {
      await api.updateDocumentComment(document.id, commentId, { status: 'resolved' })
      loadComments()
      toast.success('Comment resolved')
    } catch (error) {
      console.error('Failed to resolve comment:', error)
      toast.error('Failed to resolve comment')
    }
  }

  // Document Approval Workflow functions (Issue #50)
  const loadApprovals = async () => {
    setLoadingApprovals(true)
    try {
      const response = await api.getDocumentApprovals(document.id)
      if (response.success) {
        setApprovals(response.approvals as DocumentApproval[])
      }
    } catch (error) {
      console.error('Failed to load approvals:', error)
    } finally {
      setLoadingApprovals(false)
    }
  }

  const loadUsers = async () => {
    try {
      const response = await api.getUsers()
      if (response.success) {
        // Only show admins and contributors as potential approvers
        setUsers(response.users.filter((u: any) => u.role !== 'viewer'))
      }
    } catch (error) {
      console.error('Failed to load users:', error)
    }
  }

  const handleRequestApproval = async () => {
    if (!selectedApproverId) {
      toast.error('Please select an approver')
      return
    }

    setRequestingApproval(true)
    try {
      const response = await api.requestDocumentApproval(document.id, {
        approver_id: selectedApproverId,
        comments: approvalComments || undefined,
      })
      if (response.success) {
        toast.success('Approval requested successfully')
        loadApprovals()
        setSelectedApproverId('')
        setApprovalComments('')
        onSave() // Refresh document to update approval_status
      }
    } catch (error: any) {
      console.error('Failed to request approval:', error)
      toast.error(error.message || 'Failed to request approval')
    } finally {
      setRequestingApproval(false)
    }
  }

  const handleApproveDocument = async (approvalId?: string) => {
    const confirmed = await confirm({
      title: 'Approve Document',
      message: 'Are you sure you want to approve this document? It will be marked as approved.',
      type: 'info',
      confirmText: 'Approve',
    })
    if (!confirmed) return

    try {
      const response = await api.approveDocument(document.id, {
        comments: approvalComments || undefined,
      })
      if (response.success) {
        toast.success('Document approved')
        loadApprovals()
        setApprovalComments('')
        onSave() // Refresh document
      }
    } catch (error: any) {
      console.error('Failed to approve document:', error)
      toast.error(error.message || 'Failed to approve document')
    }
  }

  const handleRejectDocument = async () => {
    if (!approvalComments.trim()) {
      toast.error('Please provide a reason for rejection')
      return
    }

    const confirmed = await confirm({
      title: 'Reject Document',
      message: 'Are you sure you want to reject this document? The requester will be notified.',
      type: 'danger',
      confirmText: 'Reject',
    })
    if (!confirmed) return

    try {
      const response = await api.rejectDocument(document.id, {
        comments: approvalComments,
      })
      if (response.success) {
        toast.success('Document rejected')
        loadApprovals()
        setApprovalComments('')
        onSave() // Refresh document
      }
    } catch (error: any) {
      console.error('Failed to reject document:', error)
      toast.error(error.message || 'Failed to reject document')
    }
  }

  const handleRecallApproval = async () => {
    const confirmed = await confirm({
      title: 'Recall Approval Request',
      message: 'Are you sure you want to recall this approval request?',
      type: 'warning',
      confirmText: 'Recall',
    })
    if (!confirmed) return

    try {
      const response = await api.recallDocumentApproval(document.id)
      if (response.success) {
        toast.success('Approval request recalled')
        loadApprovals()
        onSave() // Refresh document
      }
    } catch (error: any) {
      console.error('Failed to recall approval:', error)
      toast.error(error.message || 'Failed to recall approval')
    }
  }

  // Get pending approval for this user (if they are an approver)
  const pendingApprovalForUser = approvals.find(
    a => a.status === 'pending' && a.approver_id === user?.id
  )

  // Get pending approval request (if user requested it)
  const pendingApprovalRequest = approvals.find(
    a => a.status === 'pending' && a.requested_by === user?.id
  )

  useEffect(() => {
    setContent(version?.content || '')
  }, [version])

  useEffect(() => {
    if (showComments) {
      loadComments()
    }
  }, [showComments])

  // Load approvals and users when approval panel is shown (Issue #50)
  useEffect(() => {
    if (showApprovalPanel) {
      loadApprovals()
      loadUsers()
    }
  }, [showApprovalPanel])

  const handleRegenerate = async () => {
    const confirmed = await confirm({
      title: 'Regenerate Document',
      message: 'This will regenerate the document content using AI and create a new version. The current content will be preserved in version history. Continue?',
      type: 'warning',
      confirmText: 'Regenerate',
    })
    if (!confirmed) return

    setRegenerating(true)
    try {
      const response = await api.regenerateDocument(document.id)
      if (response.success) {
        setContent(response.content)
        // Check if this was a fallback (template) regeneration
        if (response.generation?.isFallback) {
          toast.warning(
            `Document regenerated with template (v${response.version}). AI was temporarily unavailable - please customize the placeholder content.`,
            6000
          )
        } else {
          toast.success(`Document regenerated! New version: v${response.version}`)
        }
        onSave() // Refresh the document list and versions
      }
    } catch (error) {
      console.error('Failed to regenerate:', error)
      toast.error('Failed to regenerate document')
    } finally {
      setRegenerating(false)
    }
  }

  const handleSave = async (forceSave = false) => {
    setSaving(true)
    try {
      // Create new version if content changed
      if (content !== version?.content) {
        const result = await api.createDocumentVersion(document.id, {
          content,
          change_summary: 'Updated content',
          change_type: 'minor',
          expected_version: startingVersion,
          force: forceSave,
        })

        // Check for conflict response (Issue #177)
        if (!result.success && result.conflict_type) {
          setConflictData({
            conflict_type: result.conflict_type,
            locked_by: result.locked_by,
            lock_expires_at: result.lock_expires_at,
            expected_version: result.expected_version,
            current_version: result.current_version,
            current_content: result.current_content,
            conflicting_edit: result.conflicting_edit,
          })
          setShowConflictModal(true)
          setSaving(false)
          return
        }

        // Update starting version after successful save
        if (result.version) {
          setStartingVersion(result.version)
        }
      }

      // Collect updates for the document
      const updates: Record<string, any> = {}

      // Update status if changed
      if (status !== document.status) {
        updates.status = status
      }

      // Update expiration if changed
      const newExpiresAt = expiresAt ? new Date(expiresAt).getTime() : null
      if (newExpiresAt !== document.expires_at) {
        updates.expires_at = newExpiresAt
      }
      if (expirationReason !== (document.expiration_reason || '')) {
        updates.expiration_reason = expirationReason || null
      }

      // Update review frequency if changed (#102)
      if (reviewFrequency !== (document.review_frequency || '')) {
        updates.review_frequency = reviewFrequency || null
      }

      // Apply updates if any
      if (Object.keys(updates).length > 0) {
        await api.updateDocument(document.id, updates)
      }

      toast.success('Document saved successfully!')
      setEditing(false)
      setShowExpirationPanel(false)
      setShowReviewPanel(false)
      // Release lock after successful save if user has it
      if (hasLock) {
        handleReleaseLock()
      }
      onSave()
    } catch (error: any) {
      console.error('Failed to save:', error)
      // Check if the error response contains conflict data
      if (error.response?.data?.conflict_type) {
        setConflictData(error.response.data)
        setShowConflictModal(true)
      } else {
        toast.error('Failed to save document')
      }
    } finally {
      setSaving(false)
    }
  }

  // Handle conflict resolution options (Issue #177)
  const handleResolveConflict = async (resolution: 'force' | 'reload' | 'merge') => {
    setShowConflictModal(false)

    if (resolution === 'force') {
      // Force save - overwrite the other user's changes
      handleSave(true)
    } else if (resolution === 'reload') {
      // Reload - discard local changes and load the latest version
      if (conflictData?.current_content) {
        setContent(conflictData.current_content)
        setStartingVersion(conflictData.current_version || document.current_version)
        toast.success('Loaded latest version')
      }
      onSave() // Refresh to get latest versions
    } else if (resolution === 'merge') {
      // Copy the server content to clipboard for manual merge
      if (conflictData?.current_content) {
        navigator.clipboard.writeText(conflictData.current_content)
        toast.success('Server content copied to clipboard. You can now manually merge the changes.')
      }
    }

    setConflictData(null)
  }

  // Document title editing state (Issue #324)
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleValue, setTitleValue] = useState(document.title)
  const [savingTitle, setSavingTitle] = useState(false)

  const handleSaveTitle = async () => {
    if (!titleValue.trim() || titleValue === document.title) {
      setEditingTitle(false)
      setTitleValue(document.title)
      return
    }
    setSavingTitle(true)
    try {
      const response = await api.updateDocument(document.id, { title: titleValue.trim() })
      if (response.success) {
        toast.success('Title updated')
        onSave() // Refresh document list
      } else {
        toast.error('Failed to update title')
        setTitleValue(document.title)
      }
    } catch (error) {
      toast.error('Failed to update title')
      setTitleValue(document.title)
    } finally {
      setSavingTitle(false)
      setEditingTitle(false)
    }
  }

  // Render inline (not with portal) to match ConfirmModal pattern - Issue #324
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60"
      onClick={onClose}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 gap-4">
          <div className="flex-1">
            {/* Document Title - Issue #324 */}
            <div className="flex items-center gap-2 mb-2">
              {editingTitle ? (
                <>
                  <input
                    type="text"
                    value={titleValue}
                    onChange={(e) => setTitleValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveTitle()
                      if (e.key === 'Escape') {
                        setEditingTitle(false)
                        setTitleValue(document.title)
                      }
                    }}
                    className="flex-1 text-xl font-semibold text-gray-900 border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    autoFocus
                    disabled={savingTitle}
                  />
                  <button
                    onClick={handleSaveTitle}
                    disabled={savingTitle}
                    className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded"
                    title="Save"
                  >
                    {savingTitle ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle className="h-5 w-5" />}
                  </button>
                  <button
                    onClick={() => {
                      setEditingTitle(false)
                      setTitleValue(document.title)
                    }}
                    disabled={savingTitle}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    title="Cancel"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </>
              ) : (
                <>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {document.title || 'Untitled Document'}
                  </h2>
                  <button
                    onClick={() => setEditingTitle(true)}
                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded flex-shrink-0"
                    title="Edit title"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${categoryColors[document.category]}`}>
                {categoryLabels[document.category]}
              </span>
              <select
                value={version?.version || ''}
                onChange={(e) => {
                  const v = versions.find((v) => v.version === parseInt(e.target.value))
                  if (v) onVersionChange(v)
                }}
                className="text-sm border-gray-300 rounded"
              >
                {versions.map((v) => (
                  <option key={v.version} value={v.version}>
                    v{v.version} - {formatDate(v.created_at)}
                  </option>
                ))}
              </select>
              {/* Lock status badge (Issue #177) */}
              {lockStatus?.is_locked && (
                <span className={`flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                  hasLock
                    ? 'bg-green-100 text-green-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  <Lock className="h-3 w-3" />
                  {hasLock ? 'You have the lock' : `Editing: ${lockStatus.lock?.locked_by_name}`}
                </span>
              )}
              {/* Active editors indicator (Issue #177) */}
              {lockStatus && lockStatus.active_editors.length > 0 && (
                <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700" title={lockStatus.active_editors.map(e => e.name).join(', ')}>
                  <Users className="h-3 w-3" />
                  {lockStatus.active_editors.length} viewing
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* Lock/Unlock controls (Issue #177) */}
            {!lockStatus?.is_locked && (
              <button
                onClick={handleAcquireLock}
                disabled={acquiringLock}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors disabled:opacity-50"
                title="Acquire edit lock"
              >
                {acquiringLock ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Lock className="h-4 w-4" />
                )}
                Lock for Editing
              </button>
            )}
            {hasLock && (
              <button
                onClick={handleReleaseLock}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                title="Release edit lock"
              >
                <Unlock className="h-4 w-4" />
                Release Lock
              </button>
            )}
            {lockStatus?.is_locked && !hasLock && (
              <>
                <button
                  onClick={handleRequestAccess}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  title="Request edit access"
                >
                  <Bell className="h-4 w-4" />
                  Request Access
                </button>
                {user?.role === 'admin' && (
                  <button
                    onClick={handleForceUnlock}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                    title="Force unlock (admin)"
                  >
                    <Unlock className="h-4 w-4" />
                    Force Unlock
                  </button>
                )}
              </>
            )}
            {/* Comments button (Issue #177) */}
            <button
              onClick={() => setShowComments(!showComments)}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                showComments
                  ? 'text-white bg-gray-700'
                  : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
              }`}
              title="View comments"
            >
              <MessageSquare className="h-4 w-4" />
              Comments
              {comments.filter(c => c.status === 'open').length > 0 && (
                <span className="px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                  {comments.filter(c => c.status === 'open').length}
                </span>
              )}
            </button>
            {/* Expiration button with indicator */}
            <button
              onClick={() => setShowExpirationPanel(!showExpirationPanel)}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                showExpirationPanel
                  ? 'text-white bg-gray-700'
                  : document.expires_at
                    ? getExpirationStatus(document.expires_at) === 'expired'
                      ? 'text-red-700 bg-red-50 hover:bg-red-100'
                      : getExpirationStatus(document.expires_at) === 'expiring_soon'
                        ? 'text-amber-700 bg-amber-50 hover:bg-amber-100'
                        : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                    : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
              }`}
              title="Set expiration date"
            >
              {document.expires_at && getExpirationStatus(document.expires_at) === 'expired' ? (
                <AlertTriangle className="h-4 w-4" />
              ) : (
                <Calendar className="h-4 w-4" />
              )}
              {document.expires_at
                ? getExpirationStatus(document.expires_at) === 'expired'
                  ? 'Expired'
                  : getExpirationStatus(document.expires_at) === 'expiring_soon'
                    ? `Expires in ${getDaysUntilExpiration(document.expires_at)}d`
                    : 'Expiration'
                : 'Set Expiration'
              }
            </button>
            {/* Review Schedule button (#102) */}
            <button
              onClick={() => setShowReviewPanel(!showReviewPanel)}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                showReviewPanel
                  ? 'text-white bg-gray-700'
                  : document.next_review_date
                    ? getReviewStatus(document.next_review_date) === 'overdue'
                      ? 'text-red-700 bg-red-50 hover:bg-red-100'
                      : getReviewStatus(document.next_review_date) === 'due_soon'
                        ? 'text-amber-700 bg-amber-50 hover:bg-amber-100'
                        : 'text-blue-700 bg-blue-50 hover:bg-blue-100'
                    : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
              }`}
              title="Set review schedule"
            >
              <Clock className="h-4 w-4" />
              {document.next_review_date
                ? getReviewStatus(document.next_review_date) === 'overdue'
                  ? `Review Overdue (${Math.abs(getDaysUntilReview(document.next_review_date) || 0)}d)`
                  : getReviewStatus(document.next_review_date) === 'due_soon'
                    ? `Review in ${getDaysUntilReview(document.next_review_date)}d`
                    : 'Review Schedule'
                : 'Set Review'
              }
            </button>
            {/* Approval Workflow button (Issue #50) */}
            <button
              onClick={() => setShowApprovalPanel(!showApprovalPanel)}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                showApprovalPanel
                  ? 'text-white bg-gray-700'
                  : document.approval_status === 'pending'
                    ? 'text-amber-700 bg-amber-50 hover:bg-amber-100'
                    : document.approval_status === 'approved'
                      ? 'text-green-700 bg-green-50 hover:bg-green-100'
                      : document.approval_status === 'rejected'
                        ? 'text-red-700 bg-red-50 hover:bg-red-100'
                        : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
              }`}
              title="Document approval workflow"
            >
              <ShieldCheck className="h-4 w-4" />
              {document.approval_status === 'pending'
                ? 'Pending Approval'
                : document.approval_status === 'approved'
                  ? 'Approved'
                  : document.approval_status === 'rejected'
                    ? 'Rejected'
                    : 'Approval'
              }
              {pendingApprovalForUser && (
                <span className="px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">
                  Action Required
                </span>
              )}
            </button>
            {/* Export button */}
            {!version?.file_key && content && (
              <button
                onClick={() => setShowExport(!showExport)}
                className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  showExport
                    ? 'text-white bg-gray-700'
                    : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                }`}
                title="Export document"
              >
                <FileDown className="h-4 w-4" />
                Export
              </button>
            )}
            {/* Save as Template button (Issue #92) */}
            {!version?.file_key && content && onSaveAsTemplate && (
              <button
                onClick={() => setShowSaveAsTemplateModal(true)}
                className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  showSaveAsTemplateModal
                    ? 'text-white bg-gray-700'
                    : 'text-purple-700 bg-purple-50 hover:bg-purple-100'
                }`}
                title="Save this document as a reusable template"
              >
                <LayoutTemplate className="h-4 w-4" />
                Save as Template
              </button>
            )}
            {/* Regenerate button */}
            {!version?.file_key && (
              <button
                onClick={handleRegenerate}
                disabled={regenerating || saving || (lockStatus?.is_locked && !hasLock)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors disabled:opacity-50"
                title={lockStatus?.is_locked && !hasLock ? 'Document is locked by another user' : 'Regenerate document content with AI'}
              >
                {regenerating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {regenerating ? 'Regenerating...' : 'Regenerate'}
              </button>
            )}
            {/* View/Edit toggle */}
            {!version?.file_key && (
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setEditing(false)}
                  className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    !editing
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Eye className="h-4 w-4" />
                  Preview
                </button>
                <button
                  onClick={() => {
                    if (lockStatus?.is_locked && !hasLock) {
                      toast.error(`Document is being edited by ${lockStatus.lock?.locked_by_name}. Request access or wait for them to finish.`)
                      return
                    }
                    if (!hasLock) {
                      handleAcquireLock().then(() => {
                        setEditing(true)
                      })
                    } else {
                      setEditing(true)
                    }
                  }}
                  disabled={lockStatus?.is_locked && !hasLock}
                  className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    editing
                      ? 'bg-white text-gray-900 shadow-sm'
                      : lockStatus?.is_locked && !hasLock
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title={lockStatus?.is_locked && !hasLock ? `Locked by ${lockStatus.lock?.locked_by_name}` : 'Edit document'}
                >
                  {lockStatus?.is_locked && !hasLock ? (
                    <Lock className="h-4 w-4" />
                  ) : (
                    <Pencil className="h-4 w-4" />
                  )}
                  Edit
                </button>
              </div>
            )}
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as any)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
            >
              <option value="draft">Draft</option>
              <option value="in-review">In Review</option>
              <option value="approved">Approved</option>
              <option value="archived">Archived</option>
            </select>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto" data-color-mode="light">
          {/* Expiration Panel - shown when expiration is toggled */}
          {showExpirationPanel && (
            <div className="p-6 border-b border-gray-200 bg-amber-50">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-amber-600" />
                  <h3 className="font-medium text-gray-900">Document Expiration / Sunset Date</h3>
                </div>
                <button
                  onClick={() => setShowExpirationPanel(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Set an expiration date for this document. You will be notified when documents are expiring or have expired.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expiration Date
                  </label>
                  <input
                    type="date"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expiration Reason (optional)
                  </label>
                  <input
                    type="text"
                    value={expirationReason}
                    onChange={(e) => setExpirationReason(e.target.value)}
                    placeholder="e.g., Regulatory update required"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              {expiresAt && (
                <div className="mt-4 flex items-center gap-2">
                  {(() => {
                    const expStatus = getExpirationStatus(new Date(expiresAt).getTime())
                    const daysLeft = getDaysUntilExpiration(new Date(expiresAt).getTime())
                    if (expStatus === 'expired') {
                      return (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                          <AlertTriangle className="h-4 w-4" />
                          This document is expired
                        </span>
                      )
                    } else if (expStatus === 'expiring_soon') {
                      return (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                          <Clock className="h-4 w-4" />
                          Expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                        </span>
                      )
                    } else {
                      return (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                          <Calendar className="h-4 w-4" />
                          {daysLeft} day{daysLeft !== 1 ? 's' : ''} until expiration
                        </span>
                      )
                    }
                  })()}
                  <button
                    onClick={() => {
                      setExpiresAt('')
                      setExpirationReason('')
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Clear expiration
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Review Schedule Panel - shown when review is toggled (#102) */}
          {showReviewPanel && (
            <div className="p-6 border-b border-gray-200 bg-blue-50">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-600" />
                  <h3 className="font-medium text-gray-900">Document Review Schedule</h3>
                </div>
                <button
                  onClick={() => setShowReviewPanel(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Set a recurring review schedule for this document. You will be notified when documents are due for review.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Review Frequency
                  </label>
                  <select
                    value={reviewFrequency}
                    onChange={(e) => setReviewFrequency(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">No scheduled review</option>
                    {Object.entries(reviewFrequencyLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Review Status
                  </label>
                  <div className="flex items-center gap-2 px-3 py-2">
                    {document.next_review_date ? (
                      <>
                        {(() => {
                          const reviewStat = getReviewStatus(document.next_review_date)
                          const daysUntil = getDaysUntilReview(document.next_review_date)
                          if (reviewStat === 'overdue') {
                            return (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                                <AlertTriangle className="h-4 w-4" />
                                Overdue by {Math.abs(daysUntil || 0)} day{Math.abs(daysUntil || 0) !== 1 ? 's' : ''}
                              </span>
                            )
                          } else if (reviewStat === 'due_soon') {
                            return (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                                <Clock className="h-4 w-4" />
                                Due in {daysUntil} day{daysUntil !== 1 ? 's' : ''}
                              </span>
                            )
                          } else {
                            return (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                                <Calendar className="h-4 w-4" />
                                Next review: {formatDate(document.next_review_date)}
                              </span>
                            )
                          }
                        })()}
                      </>
                    ) : (
                      <span className="text-sm text-gray-500">No review scheduled</span>
                    )}
                  </div>
                </div>
              </div>
              {document.last_review_date && (
                <div className="mt-4 text-sm text-gray-600">
                  Last reviewed: {formatDate(document.last_review_date)}
                </div>
              )}
            </div>
          )}

          {/* Approval Workflow Panel (Issue #50) */}
          {showApprovalPanel && (
            <div className="p-6 border-b border-gray-200 bg-emerald-50">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-emerald-600" />
                  <h3 className="font-medium text-gray-900">Document Approval Workflow</h3>
                </div>
                <button
                  onClick={() => setShowApprovalPanel(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {loadingApprovals ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Current Approval Status */}
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-700">Current Status:</span>
                    {document.approval_status === 'pending' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                        <Clock className="h-4 w-4" />
                        Pending Approval
                      </span>
                    ) : document.approval_status === 'approved' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                        <CheckCircle className="h-4 w-4" />
                        Approved
                      </span>
                    ) : document.approval_status === 'rejected' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                        <XCircle className="h-4 w-4" />
                        Rejected
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                        No approval required
                      </span>
                    )}
                  </div>

                  {/* Approve/Reject Actions (for assigned approver) */}
                  {pendingApprovalForUser && (
                    <div className="p-4 bg-amber-100 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2 mb-3">
                        <UserCheck className="h-5 w-5 text-amber-700" />
                        <span className="font-medium text-amber-800">You have been requested to approve this document</span>
                      </div>
                      <p className="text-sm text-amber-700 mb-3">
                        Requested by: {pendingApprovalForUser.requester_name} on {formatDate(pendingApprovalForUser.requested_at)}
                      </p>
                      {pendingApprovalForUser.comments && (
                        <p className="text-sm text-amber-700 mb-3 italic">
                          "{pendingApprovalForUser.comments}"
                        </p>
                      )}
                      <div className="space-y-3">
                        <textarea
                          value={approvalComments}
                          onChange={(e) => setApprovalComments(e.target.value)}
                          placeholder="Add comments (required for rejection)..."
                          className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                          rows={2}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApproveDocument()}
                            className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                          >
                            <CheckCircle className="h-4 w-4" />
                            Approve
                          </button>
                          <button
                            onClick={handleRejectDocument}
                            className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                          >
                            <XCircle className="h-4 w-4" />
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Recall Option (for requester) */}
                  {pendingApprovalRequest && !pendingApprovalForUser && (
                    <div className="p-4 bg-blue-100 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-blue-800">Approval request pending</p>
                          <p className="text-sm text-blue-700">
                            Waiting for {pendingApprovalRequest.approver_name} to review
                          </p>
                        </div>
                        <button
                          onClick={handleRecallApproval}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Undo2 className="h-4 w-4" />
                          Recall Request
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Request Approval Form (when no pending approval) */}
                  {!pendingApprovalForUser && !pendingApprovalRequest && document.approval_status !== 'pending' && (
                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                      <h4 className="font-medium text-gray-900 mb-3">Request Approval</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Select Approver
                          </label>
                          <select
                            value={selectedApproverId}
                            onChange={(e) => setSelectedApproverId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                          >
                            <option value="">Choose an approver...</option>
                            {users
                              .filter(u => u.id !== user?.id) // Don't show self
                              .map(u => (
                                <option key={u.id} value={u.id}>
                                  {u.first_name && u.last_name
                                    ? `${u.first_name} ${u.last_name}`
                                    : u.username
                                  } ({u.role})
                                </option>
                              ))
                            }
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Comments (optional)
                          </label>
                          <input
                            type="text"
                            value={approvalComments}
                            onChange={(e) => setApprovalComments(e.target.value)}
                            placeholder="Why is approval needed?"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                      </div>
                      <button
                        onClick={handleRequestApproval}
                        disabled={requestingApproval || !selectedApproverId}
                        className="flex items-center gap-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {requestingApproval ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                        Request Approval
                      </button>
                    </div>
                  )}

                  {/* Approval History */}
                  {approvals.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Approval History</h4>
                      <div className="space-y-2">
                        {approvals.map((approval) => (
                          <div
                            key={approval.id}
                            className="p-3 bg-white rounded-lg border border-gray-200 flex items-center justify-between"
                          >
                            <div className="flex items-center gap-3">
                              {approval.status === 'pending' ? (
                                <Clock className="h-5 w-5 text-amber-500" />
                              ) : approval.status === 'approved' ? (
                                <CheckCircle className="h-5 w-5 text-green-500" />
                              ) : approval.status === 'rejected' ? (
                                <XCircle className="h-5 w-5 text-red-500" />
                              ) : (
                                <Undo2 className="h-5 w-5 text-gray-500" />
                              )}
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  v{approval.version} - {approval.status.charAt(0).toUpperCase() + approval.status.slice(1)}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {approval.status === 'pending'
                                    ? `Requested from ${approval.approver_name} by ${approval.requester_name}`
                                    : `${approval.approver_name} ${approval.status}`}
                                  {' • '}
                                  {formatDateTime(approval.responded_at || approval.requested_at)}
                                </p>
                                {approval.comments && (
                                  <p className="text-xs text-gray-600 mt-1 italic">"{approval.comments}"</p>
                                )}
                              </div>
                            </div>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              approval.status === 'pending'
                                ? 'bg-amber-100 text-amber-700'
                                : approval.status === 'approved'
                                  ? 'bg-green-100 text-green-700'
                                  : approval.status === 'rejected'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-gray-100 text-gray-700'
                            }`}>
                              {approval.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Export Panel - shown when export is toggled */}
          {showExport && !version?.file_key && (
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <DocumentExport
                title={document.title}
                content={content}
                category={document.category}
                version={version?.version || document.current_version}
                lastModified={version?.created_at ? formatDate(version.created_at) : undefined}
                author={version?.created_by_username}
                onClose={() => setShowExport(false)}
              />
            </div>
          )}

          {/* Save as Template Panel (Issue #92) - shown when save as template is toggled */}
          {showSaveAsTemplateModal && onSaveAsTemplate && (
            <div className="p-6 border-b border-gray-200 bg-purple-50">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <LayoutTemplate className="h-5 w-5 text-purple-600" />
                  <h3 className="font-medium text-gray-900">Save as Template</h3>
                </div>
                <button
                  onClick={() => setShowSaveAsTemplateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Create a reusable template from this document. The content will be copied and you can add variable placeholders like {"{{company_name}}"} or {"{{date}}"}.
              </p>
              <SaveAsTemplateForm
                initialName={`${document.title} Template`}
                initialDescription={document.description || ''}
                initialContent={content}
                initialCategory={document.category}
                saving={savingAsTemplate}
                onSave={async (data) => {
                  setSavingAsTemplate(true)
                  try {
                    await onSaveAsTemplate(data)
                    setShowSaveAsTemplateModal(false)
                    toast.success('Template created successfully!')
                  } catch (error) {
                    console.error('Failed to save template:', error)
                    toast.error('Failed to save template')
                  } finally {
                    setSavingAsTemplate(false)
                  }
                }}
                onCancel={() => setShowSaveAsTemplateModal(false)}
              />
            </div>
          )}

          {/* Comments Panel (Issue #177) - shown when comments is toggled */}
          {showComments && (
            <div className="p-6 border-b border-gray-200 bg-purple-50">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-purple-600" />
                  <h3 className="font-medium text-gray-900">Document Comments</h3>
                </div>
                <button
                  onClick={() => setShowComments(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Add comment input */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleAddComment()
                    }
                  }}
                />
                <button
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  Send
                </button>
              </div>

              {/* Comments list */}
              {loadingComments ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
                </div>
              ) : comments.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No comments yet. Be the first to comment!</p>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {comments.map((comment) => (
                    <div
                      key={comment.id}
                      className={`p-3 rounded-lg ${
                        comment.status === 'resolved'
                          ? 'bg-gray-100 opacity-60'
                          : 'bg-white border border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-gray-900">{comment.author_name}</span>
                          <span className="text-xs text-gray-500">
                            {formatDateTime(comment.created_at)}
                          </span>
                          {comment.status === 'resolved' && (
                            <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">Resolved</span>
                          )}
                        </div>
                        {comment.status === 'open' && (
                          <button
                            onClick={() => handleResolveComment(comment.id)}
                            className="text-xs text-gray-500 hover:text-green-600"
                          >
                            Resolve
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 mt-1">{comment.comment_text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {version?.file_key ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-6">
              <FileText className="h-16 w-16 text-gray-300 mb-4" />
              <p className="text-gray-600 mb-4">This version is a file upload</p>
              <a
                href={api.getDocumentDownloadUrl(document.id, version.version)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
                target="_blank"
              >
                <Download className="h-4 w-4" />
                Download File
              </a>
            </div>
          ) : editing ? (
            <Suspense fallback={<EditorSkeleton />}>
              <LazyMDEditor
                value={content}
                onChange={(val) => setContent(val || '')}
                height="100%"
                style={{ minHeight: '500px' }}
                preview="edit"
              />
            </Suspense>
          ) : (
            <div className="p-6 space-y-6">
              {/* AI Suggested Controls Panel */}
              <SuggestedControlsPanel
                documentId={document.id}
                documentCategory={document.category}
                onLinksUpdated={onSave}
              />

              {/* Linked Controls Panel (Issue #212) */}
              <LinkedControlsPanel documentId={document.id} onLinksUpdated={onSave} />

              {/* Document Content */}
              <Suspense fallback={<EditorSkeleton />}>
                <LazyMDEditorMarkdown
                  source={content || '*No content*'}
                  style={{
                    backgroundColor: 'transparent',
                    padding: '0',
                  }}
                />
              </Suspense>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            {version && (
              <>
                Last edited by {version.created_by_username} on{' '}
                {formatDateTime(version.created_at)}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {(editing && content !== version?.content) ||
             (expiresAt !== (document.expires_at ? new Date(document.expires_at).toISOString().split('T')[0] : '')) ||
             (expirationReason !== (document.expiration_reason || '')) ? (
              <span className="text-sm text-amber-600 mr-2">Unsaved changes</span>
            ) : null}
            <button
              onClick={async () => {
                // Release lock if we have it when closing
                if (hasLock) {
                  await handleReleaseLock()
                }
                setContent(version?.content || '')
                setExpiresAt(document.expires_at ? new Date(document.expires_at).toISOString().split('T')[0] : '')
                setExpirationReason(document.expiration_reason || '')
                setEditing(false)
                setShowExpirationPanel(false)
                onClose()
              }}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {editing ? 'Discard Changes' : 'Close'}
            </button>
            {(editing ||
              content !== version?.content ||
              status !== document.status ||
              expiresAt !== (document.expires_at ? new Date(document.expires_at).toISOString().split('T')[0] : '') ||
              expirationReason !== (document.expiration_reason || '')) && (
              <button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save Changes
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Conflict Resolution Modal (Issue #177) */}
      {showConflictModal && conflictData && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <GitMerge className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {conflictData.conflict_type === 'locked'
                      ? 'Document Locked'
                      : 'Edit Conflict Detected'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {conflictData.conflict_type === 'locked'
                      ? `This document is being edited by ${conflictData.locked_by}`
                      : 'Another user has saved changes since you started editing'}
                  </p>
                </div>
              </div>

              {conflictData.conflict_type === 'version_conflict' && conflictData.conflicting_edit && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <span className="text-sm font-medium text-gray-900">Conflicting Changes</span>
                  </div>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>
                      <strong>Version {conflictData.conflicting_edit.version}</strong> was saved by{' '}
                      <strong>{conflictData.conflicting_edit.created_by_name}</strong>
                    </p>
                    <p>
                      {formatDateTime(conflictData.conflicting_edit.created_at)}
                    </p>
                    {conflictData.conflicting_edit.change_summary && (
                      <p className="italic">"{conflictData.conflicting_edit.change_summary}"</p>
                    )}
                  </div>
                  <p className="mt-3 text-xs text-gray-500">
                    You were editing version {conflictData.expected_version}, but the document is now at version {conflictData.current_version}.
                  </p>
                </div>
              )}

              {conflictData.conflict_type === 'locked' && conflictData.lock_expires_at && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600">
                    The lock will expire in approximately{' '}
                    <strong>
                      {Math.max(0, Math.ceil((conflictData.lock_expires_at - Date.now()) / 60000))} minutes
                    </strong>.
                  </p>
                </div>
              )}

              <div className="space-y-3">
                {conflictData.conflict_type === 'version_conflict' && (
                  <>
                    <button
                      onClick={() => handleResolveConflict('reload')}
                      className="w-full flex items-center gap-3 p-3 text-left rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <RefreshCw className="h-5 w-5 text-blue-600" />
                      <div>
                        <div className="font-medium text-gray-900">Load Their Changes</div>
                        <div className="text-sm text-gray-500">Discard your edits and use the latest version</div>
                      </div>
                    </button>

                    <button
                      onClick={() => handleResolveConflict('merge')}
                      className="w-full flex items-center gap-3 p-3 text-left rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <Copy className="h-5 w-5 text-purple-600" />
                      <div>
                        <div className="font-medium text-gray-900">Copy Server Version</div>
                        <div className="text-sm text-gray-500">Copy server content to clipboard for manual merge</div>
                      </div>
                    </button>

                    <button
                      onClick={() => handleResolveConflict('force')}
                      className="w-full flex items-center gap-3 p-3 text-left rounded-lg border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors"
                    >
                      <AlertTriangle className="h-5 w-5 text-amber-600" />
                      <div>
                        <div className="font-medium text-amber-900">Overwrite Their Changes</div>
                        <div className="text-sm text-amber-700">Save your version (their changes will be lost)</div>
                      </div>
                    </button>
                  </>
                )}

                <button
                  onClick={() => {
                    setShowConflictModal(false)
                    setConflictData(null)
                  }}
                  className="w-full px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Linked Controls Panel Component (Issue #212, #311)
function LinkedControlsPanel({
  documentId,
  onLinksUpdated,
}: {
  documentId: string
  onLinksUpdated?: () => void
}) {
  const [linkedControls, setLinkedControls] = useState<LinkedControl[]>([])
  const [linkedInternalControls, setLinkedInternalControls] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState(true)
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [availableControls, setAvailableControls] = useState<any[]>([])
  const [availableInternalControls, setAvailableInternalControls] = useState<any[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedControlIds, setSelectedControlIds] = useState<Set<string>>(new Set())
  const [selectedInternalControlIds, setSelectedInternalControlIds] = useState<Set<string>>(new Set())
  const [linking, setLinking] = useState(false)
  const [linkTab, setLinkTab] = useState<'internal' | 'framework'>('internal') // #311: Default to internal controls
  const toast = useToast()
  const confirm = useConfirm()

  useEffect(() => {
    loadLinkedControls()
  }, [documentId])

  const loadLinkedControls = async () => {
    setLoading(true)
    try {
      // Load framework controls
      const response = await api.getDocumentControls(documentId)
      if (response.success) {
        setLinkedControls(response.controls || [])
      }
      // #311: Load linked internal controls
      const icResponse = await api.getDocumentInternalControls(documentId)
      if (icResponse.success) {
        setLinkedInternalControls(icResponse.internal_controls || [])
      }
    } catch (error) {
      console.error('Error loading linked controls:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadAvailableControls = async () => {
    try {
      // Load framework controls
      const response = await api.getControls({ limit: 500 })
      if (response.success) {
        // Filter out already linked controls
        const linkedIds = new Set(linkedControls.map((c) => c.control_id))
        setAvailableControls(
          (response.controls || []).filter((c: any) => !linkedIds.has(c.id))
        )
      }
      // #311: Load internal controls
      const icResponse = await api.getInternalControls({ limit: 200 })
      if (icResponse.success) {
        const linkedIcIds = new Set(linkedInternalControls.map((ic: any) => ic.id))
        setAvailableInternalControls(
          (icResponse.data || []).filter((ic: any) => !linkedIcIds.has(ic.id))
        )
      }
    } catch (error) {
      console.error('Error loading controls:', error)
    }
  }

  const handleOpenLinkModal = async () => {
    setShowLinkModal(true)
    setSearchTerm('')
    setSelectedControlIds(new Set())
    setSelectedInternalControlIds(new Set())
    setLinkTab('internal') // #311: Default to internal controls
    await loadAvailableControls()
  }

  const handleLinkControls = async () => {
    if (selectedControlIds.size === 0) return
    setLinking(true)
    try {
      const response = await api.linkDocumentControls(documentId, {
        control_ids: Array.from(selectedControlIds),
      })
      if (response.success) {
        setShowLinkModal(false)
        await loadLinkedControls()
        onLinksUpdated?.()
      }
    } catch (error) {
      console.error('Error linking controls:', error)
    } finally {
      setLinking(false)
    }
  }

  // #311: Handle linking internal controls
  const handleLinkInternalControls = async () => {
    if (selectedInternalControlIds.size === 0) return
    setLinking(true)
    try {
      const response = await api.linkDocumentInternalControls(documentId, {
        internal_control_ids: Array.from(selectedInternalControlIds),
      })
      if (response.success) {
        toast.success(`Linked ${selectedInternalControlIds.size} internal control(s)`)
        setShowLinkModal(false)
        await loadLinkedControls()
        onLinksUpdated?.()
      }
    } catch (error) {
      console.error('Error linking internal controls:', error)
      toast.error('Failed to link internal controls')
    } finally {
      setLinking(false)
    }
  }

  const handleUnlinkControl = async (controlId: string) => {
    const confirmed = await confirm({
      title: 'Unlink Control',
      message: 'Are you sure you want to unlink this control from the document?',
      type: 'warning',
      confirmText: 'Unlink',
    })
    if (!confirmed) return
    try {
      await api.unlinkDocumentControl(documentId, controlId)
      toast.success('Control unlinked successfully')
      await loadLinkedControls()
      onLinksUpdated?.()
    } catch (error) {
      console.error('Error unlinking control:', error)
      toast.error('Failed to unlink control')
    }
  }

  const toggleControlSelection = (id: string) => {
    const newSelected = new Set(selectedControlIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedControlIds(newSelected)
  }

  // #311: Toggle internal control selection
  const toggleInternalControlSelection = (id: string) => {
    const newSelected = new Set(selectedInternalControlIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedInternalControlIds(newSelected)
  }

  const filteredControls = availableControls.filter(
    (c) =>
      c.control_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.framework_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  )

  // #311: Filter internal controls
  const filteredInternalControls = availableInternalControls.filter(
    (ic) =>
      ic.control_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ic.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ic.category || '').toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Group linked controls by framework
  const groupedControls = linkedControls.reduce((acc, control) => {
    const framework = control.framework_name || 'Other'
    if (!acc[framework]) acc[framework] = []
    acc[framework].push(control)
    return acc
  }, {} as Record<string, LinkedControl[]>)

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <Link2 className="h-5 w-5 text-blue-500" />
          <h3 className="font-medium text-gray-900">Linked Controls</h3>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {linkedControls.length} linked
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleOpenLinkModal()
            }}
            className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded"
          >
            <Plus className="h-4 w-4 mr-1" />
            Link Controls
          </button>
          {expanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-200">
          {loading ? (
            <div className="px-4 py-8 text-center text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              Loading linked controls...
            </div>
          ) : linkedControls.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500">
              <Link2 className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p>No controls linked to this document</p>
              <p className="text-sm mt-1">Click "Link Controls" to add controls</p>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {Object.entries(groupedControls).map(([framework, controls]) => (
                <div key={framework}>
                  <h4 className="text-sm font-medium text-gray-600 mb-2">{framework}</h4>
                  <div className="space-y-2">
                    {controls.map((control) => (
                      <div
                        key={control.control_id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">
                              {control.control_number}
                            </span>
                            <span
                              className={`px-2 py-0.5 rounded text-xs font-medium ${
                                control.status === 'compliant'
                                  ? 'bg-green-100 text-green-800'
                                  : control.status === 'non-compliant'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {control.status || 'Unknown'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mt-0.5 truncate">
                            {control.name}
                          </p>
                        </div>
                        <button
                          onClick={() => handleUnlinkControl(control.control_id)}
                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Unlink control"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Link Controls Modal - #311: Updated with Internal Controls tab */}
      {showLinkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Link Controls</h2>
              <button
                onClick={() => setShowLinkModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* #311: Tabs for Internal Controls vs Framework Controls */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setLinkTab('internal')}
                className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  linkTab === 'internal'
                    ? 'border-primary-500 text-primary-600 bg-primary-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span>Internal Controls</span>
                  <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">Recommended</span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">Links policy to common controls, satisfies all frameworks</p>
              </button>
              <button
                onClick={() => setLinkTab('framework')}
                className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  linkTab === 'framework'
                    ? 'border-primary-500 text-primary-600 bg-primary-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>Framework Controls</span>
                <p className="text-xs text-gray-400 mt-0.5">Legacy: links directly to specific framework requirements</p>
              </button>
            </div>

            <div className="px-6 py-4 border-b border-gray-200">
              <input
                type="text"
                placeholder={linkTab === 'internal' ? "Search internal controls..." : "Search framework controls..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="flex-1 overflow-auto p-4">
              {linkTab === 'internal' ? (
                // Internal Controls Tab
                filteredInternalControls.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    {availableInternalControls.length === 0
                      ? 'All internal controls are already linked'
                      : 'No internal controls match your search'}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredInternalControls.map((ic) => (
                      <label
                        key={ic.id}
                        className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer ${
                          selectedInternalControlIds.has(ic.id)
                            ? 'bg-green-50 border border-green-200'
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedInternalControlIds.has(ic.id)}
                          onChange={() => toggleInternalControlSelection(ic.id)}
                          className="mt-1 h-4 w-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-medium text-gray-900">
                              {ic.control_number}
                            </span>
                            <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                              {ic.category}
                            </span>
                            {ic.status === 'compliant' && (
                              <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                                Compliant
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 mt-0.5">{ic.name}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                )
              ) : (
                // Framework Controls Tab (Legacy)
                filteredControls.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    {availableControls.length === 0
                      ? 'All controls are already linked'
                      : 'No controls match your search'}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredControls.map((control) => (
                      <label
                        key={control.id}
                        className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer ${
                          selectedControlIds.has(control.id)
                            ? 'bg-blue-50 border border-blue-200'
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedControlIds.has(control.id)}
                          onChange={() => toggleControlSelection(control.id)}
                          className="mt-1 h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">
                              {control.control_number}
                            </span>
                            <span className="text-xs text-gray-500">
                              {control.framework_name}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mt-0.5">{control.name}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                )
              )}
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <span className="text-sm text-gray-500">
                {linkTab === 'internal'
                  ? `${selectedInternalControlIds.size} internal control(s) selected`
                  : `${selectedControlIds.size} framework control(s) selected`}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowLinkModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={linkTab === 'internal' ? handleLinkInternalControls : handleLinkControls}
                  disabled={(linkTab === 'internal' ? selectedInternalControlIds.size === 0 : selectedControlIds.size === 0) || linking}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {linking ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Link Selected
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// History Modal Component (Updated for Issue #63 - Version Comparison)
function HistoryModal({
  document,
  versions,
  onClose,
}: {
  document: Document
  versions: any[]
  onClose: () => void
}) {
  const { formatDateTime } = useDatePreferences()
  const [selectedVersions, setSelectedVersions] = useState<number[]>([])
  const [showDiffModal, setShowDiffModal] = useState(false)

  const toggleVersionSelection = (version: number) => {
    if (selectedVersions.includes(version)) {
      setSelectedVersions(selectedVersions.filter(v => v !== version))
    } else if (selectedVersions.length < 2) {
      setSelectedVersions([...selectedVersions, version].sort((a, b) => a - b))
    } else {
      // Replace oldest selection with new one
      setSelectedVersions([selectedVersions[1], version].sort((a, b) => a - b))
    }
  }

  const handleCompare = () => {
    if (selectedVersions.length === 2) {
      setShowDiffModal(true)
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Version History: {document.title}</h2>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Compare Versions Section */}
          {versions.length >= 2 && (
            <div className="px-6 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GitCompareArrows className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">Compare Versions</span>
                  {selectedVersions.length > 0 && (
                    <span className="text-xs text-blue-600">
                      (Selected: {selectedVersions.map(v => `v${v}`).join(' vs ')})
                    </span>
                  )}
                </div>
                <button
                  onClick={handleCompare}
                  disabled={selectedVersions.length !== 2}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    selectedVersions.length === 2
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <GitCompareArrows className="h-3.5 w-3.5" />
                  Compare
                </button>
              </div>
              {selectedVersions.length < 2 && (
                <p className="text-xs text-blue-600 mt-1">
                  Select 2 versions to compare by clicking the checkboxes
                </p>
              )}
            </div>
          )}

          <div className="flex-1 overflow-auto">
            {versions.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No version history</div>
            ) : (
              <div className="divide-y divide-gray-200">
                {versions.map((v) => (
                  <div
                    key={v.version}
                    className={`p-4 hover:bg-gray-50 cursor-pointer ${
                      selectedVersions.includes(v.version) ? 'bg-blue-50 hover:bg-blue-100' : ''
                    }`}
                    onClick={() => toggleVersionSelection(v.version)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {/* Selection checkbox */}
                        <div className={`flex items-center justify-center w-5 h-5 rounded border-2 transition-colors ${
                          selectedVersions.includes(v.version)
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : 'border-gray-300 hover:border-blue-400'
                        }`}>
                          {selectedVersions.includes(v.version) && (
                            <CheckCircle className="h-3.5 w-3.5" />
                          )}
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900">Version {v.version}</span>
                          {v.change_type && (
                            <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                              v.change_type === 'major'
                                ? 'bg-red-100 text-red-700'
                                : v.change_type === 'minor'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {v.change_type}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-sm text-gray-500">
                        {formatDateTime(v.created_at)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 ml-8">
                      {v.change_summary || 'No description'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1 ml-8">
                      By {v.created_by_username}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Diff Modal */}
      {showDiffModal && selectedVersions.length === 2 && (
        <DiffViewerModal
          documentId={document.id}
          documentTitle={document.title}
          v1={selectedVersions[0]}
          v2={selectedVersions[1]}
          onClose={() => setShowDiffModal(false)}
        />
      )}
    </>
  )
}

// Diff Viewer Modal Component (Issue #63)
function DiffViewerModal({
  documentId,
  documentTitle,
  v1,
  v2,
  onClose,
}: {
  documentId: string
  documentTitle: string
  v1: number
  v2: number
  onClose: () => void
}) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [diff, setDiff] = useState<{
    lines: Array<{
      type: 'unchanged' | 'added' | 'removed' | 'modified'
      line1?: string
      line2?: string
      lineNum1?: number
      lineNum2?: number
    }>
    summary: {
      added: number
      removed: number
      modified: number
      unchanged: number
      total: number
    }
  } | null>(null)
  const [viewMode, setViewMode] = useState<'unified' | 'split'>('unified')
  const [hideUnchanged, setHideUnchanged] = useState(false)

  useEffect(() => {
    const loadDiff = async () => {
      try {
        setLoading(true)
        const result = await api.getDocumentDiff(documentId, v1, v2)
        if (result.success) {
          setDiff(result.diff)
        } else {
          setError('Failed to load diff')
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load diff')
      } finally {
        setLoading(false)
      }
    }
    loadDiff()
  }, [documentId, v1, v2])

  const filteredLines = diff?.lines.filter(line =>
    hideUnchanged ? line.type !== 'unchanged' : true
  ) || []

  const renderLine = (line: typeof filteredLines[0], index: number) => {
    const baseClasses = 'font-mono text-sm py-1 px-3'

    if (viewMode === 'unified') {
      switch (line.type) {
        case 'added':
          return (
            <div key={index} className={`${baseClasses} bg-green-50 text-green-800 border-l-4 border-green-500`}>
              <span className="text-green-400 mr-2 select-none">+</span>
              <span className="text-gray-400 mr-2 w-8 inline-block text-right select-none">{line.lineNum2}</span>
              {line.line2}
            </div>
          )
        case 'removed':
          return (
            <div key={index} className={`${baseClasses} bg-red-50 text-red-800 border-l-4 border-red-500`}>
              <span className="text-red-400 mr-2 select-none">-</span>
              <span className="text-gray-400 mr-2 w-8 inline-block text-right select-none">{line.lineNum1}</span>
              {line.line1}
            </div>
          )
        case 'modified':
          return (
            <div key={index}>
              <div className={`${baseClasses} bg-red-50 text-red-800 border-l-4 border-red-500`}>
                <span className="text-red-400 mr-2 select-none">-</span>
                <span className="text-gray-400 mr-2 w-8 inline-block text-right select-none">{line.lineNum1}</span>
                {line.line1}
              </div>
              <div className={`${baseClasses} bg-green-50 text-green-800 border-l-4 border-green-500`}>
                <span className="text-green-400 mr-2 select-none">+</span>
                <span className="text-gray-400 mr-2 w-8 inline-block text-right select-none">{line.lineNum2}</span>
                {line.line2}
              </div>
            </div>
          )
        default:
          return (
            <div key={index} className={`${baseClasses} bg-gray-50 text-gray-700`}>
              <span className="text-gray-300 mr-2 select-none">&nbsp;</span>
              <span className="text-gray-400 mr-2 w-8 inline-block text-right select-none">{line.lineNum1}</span>
              {line.line1}
            </div>
          )
      }
    } else {
      // Split view
      return (
        <div key={index} className="flex">
          <div className={`flex-1 ${baseClasses} ${
            line.type === 'removed' || line.type === 'modified'
              ? 'bg-red-50 text-red-800'
              : 'bg-gray-50 text-gray-700'
          }`}>
            <span className="text-gray-400 mr-2 w-6 inline-block text-right select-none">
              {line.lineNum1 || ''}
            </span>
            {line.line1 || ''}
          </div>
          <div className="w-px bg-gray-300" />
          <div className={`flex-1 ${baseClasses} ${
            line.type === 'added' || line.type === 'modified'
              ? 'bg-green-50 text-green-800'
              : 'bg-gray-50 text-gray-700'
          }`}>
            <span className="text-gray-400 mr-2 w-6 inline-block text-right select-none">
              {line.lineNum2 || ''}
            </span>
            {line.line2 || ''}
          </div>
        </div>
      )
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Compare: {documentTitle}
            </h2>
            <p className="text-sm text-gray-500">
              Version {v1} → Version {v2}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-4">
            {/* View mode toggle */}
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1">
              <button
                onClick={() => setViewMode('unified')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  viewMode === 'unified'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Unified
              </button>
              <button
                onClick={() => setViewMode('split')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  viewMode === 'split'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Side-by-Side
              </button>
            </div>

            {/* Hide unchanged toggle */}
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={hideUnchanged}
                onChange={(e) => setHideUnchanged(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Hide unchanged lines
            </label>
          </div>

          {/* Summary */}
          {diff && (
            <div className="flex items-center gap-3 text-sm">
              <span className="flex items-center gap-1 text-green-700">
                <Plus className="h-4 w-4" />
                {diff.summary.added} added
              </span>
              <span className="flex items-center gap-1 text-red-700">
                <Minus className="h-4 w-4" />
                {diff.summary.removed} removed
              </span>
              <span className="flex items-center gap-1 text-yellow-700">
                <RefreshCw className="h-4 w-4" />
                {diff.summary.modified} modified
              </span>
            </div>
          )}
        </div>

        {/* Diff Content */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64 text-red-500">
              <AlertCircle className="h-5 w-5 mr-2" />
              {error}
            </div>
          ) : filteredLines.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-gray-500">
              <CheckCircle className="h-5 w-5 mr-2 text-green-500" />
              {hideUnchanged ? 'No changes to display' : 'No differences found between versions'}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredLines.map((line, index) => renderLine(line, index))}
            </div>
          )}
        </div>

        {/* Split view header */}
        {viewMode === 'split' && !loading && !error && filteredLines.length > 0 && (
          <div className="absolute top-[140px] left-6 right-6 flex">
            <div className="flex-1 text-center text-xs font-medium text-gray-500 py-1 bg-red-100 rounded-t-lg">
              Version {v1} (Old)
            </div>
            <div className="w-px" />
            <div className="flex-1 text-center text-xs font-medium text-gray-500 py-1 bg-green-100 rounded-t-lg">
              Version {v2} (New)
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Folder Modal Component (Issue #161)
function FolderModal({
  folder,
  folders,
  onClose,
  onSave,
}: {
  folder: DocumentCategory | null
  folders: DocumentCategory[]
  onClose: () => void
  onSave: (name: string, parentId?: string) => void
}) {
  const [name, setName] = useState(folder?.name || '')
  const [parentId, setParentId] = useState<string>(folder?.parent_id || '')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    onSave(name.trim(), parentId || undefined)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {folder ? 'Edit Folder' : 'New Folder'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Folder Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter folder name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              autoFocus
            />
          </div>

          {!folder && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parent Folder (optional)
              </label>
              <select
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">No parent (root level)</option>
                {folders.filter(f => !f.parent_id).map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {folder ? 'Save Changes' : 'Create Folder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
